import { esportaXLSX } from "../utils/esportaXLSX";
import CampiIndirizzo from "../components/CampiIndirizzo";
import { componiIndirizzo, erroriIndirizzo, scomponiIndirizzo, indirizzoCompletoOk } from "../utils/indirizzi";
import { useValoriCommesse, conValore } from "../utils/valoriCommesse";
import React, { useEffect, useState, useMemo, useId } from "react";
import InsightButton from "../components/InsightButton";
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, orderBy, limit, getDocs, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { prossimoProgressivo, prossimoBloccoProgressivo } from "../utils/contatoriUtils";
import { messaggioErrore } from "../utils/erroriUtente";
import { bloccaSeReadOnly } from "../utils/trialGate";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { useFeatures } from "../contexts/FeaturesContext";
import { Plus, X, Search, Building2, Phone, Mail, MapPin, Edit2, ChevronRight, User, MessageSquare } from "lucide-react";
import { calcClienteHealthScore, calcIndiceFedelta, generateBusinessInsight, generateRadarCliente, generateCustomerDNA } from "../utils/clienteHelpers";
import { MODELLI_PAGAMENTO } from "../components/ElaborazioneOffertaTab";
import ImportaExcelModal from "../components/ImportaExcelModal";
import { economiaSintetica, mappeEconomicheOrg } from "../utils/economiaCommessa";
import { SediManager, SediScheda } from "../components/SediCliente";
import { puoGestireSedi } from "../utils/sediCliente";

const STATI_CLIENTE = ["Attivo", "Potenziale", "Inattivo", "Sospeso"];
const SETTORI = ["Edilizia", "Industria", "Pubblica Amministrazione", "Energia", "Telecomunicazioni", "Trasporti", "Altro"];

// ─── Modal nuovo/modifica cliente ─────────────────────────────────
// Componente Field DEFINITO FUORI dal modal: se fosse dentro, verrebbe
// ricreato ad ogni keystroke (ogni setForm re-renderizza il genitore),
// e React perderebbe il focus sull'input ad ogni carattere digitato.
function ClienteField({ label, name, type="text", required, options, value, onChange, placeholder }) {
  const id = `cli-${useId()}`;
  return (
    <div className="form-group" style={{marginBottom:6}}>
      <label htmlFor={id} className="form-label" style={{marginBottom:2,fontSize:11}}>{label}{required && <span style={{color:"#dc2626"}}> *</span>}</label>
      {options ? (
        <select id={id} className="input" name={name} value={value||""} onChange={onChange} style={{padding:"5px 8px",fontSize:12}}>
          <option value="">Seleziona...</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : type === "textarea" ? (
        <textarea id={id} className="input" name={name} autoComplete="off" value={value||""} onChange={onChange} rows={3}/>
      ) : (
        <input id={id} className="input" type={type} name={name} placeholder={placeholder} autoComplete="off" value={value||""} onChange={onChange}/>
      )}
    </div>
  );
}

// ─── Gestione Referenti multipli (lista libera) ────────────────────────
// Definito fuori da ClienteModal per evitare il bug di perdita del focus
// (vedi fix precedente su ClienteField): se questo componente fosse
// dentro al genitore, verrebbe ricreato ad ogni keystroke.
function ReferentiManager({ referenti, onChange }) {
  const list = referenti || [];

  const addReferente = () => {
    onChange([...list, { id: Date.now().toString(), nome:"", ruolo:"", telefono:"", email:"" }]);
  };
  const updateReferente = (id, field, value) => {
    onChange(list.map(r => r.id===id ? { ...r, [field]: value } : r));
  };
  const removeReferente = id => {
    onChange(list.filter(r => r.id !== id));
  };

  return (
    <div style={{gridColumn:"1/-1"}}>
      <label className="form-label">👥 Referenti</label>
      {list.length === 0 && (
        <div style={{fontSize:12, color:"#9ca3af", marginBottom:8}}>Nessun referente aggiunto oltre a quello principale</div>
      )}
      {list.map(r => (
        <div key={r.id} style={{display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr auto", gap:8, marginBottom:8, alignItems:"center"}}>
          <input className="input" placeholder="Nome" value={r.nome} onChange={e=>updateReferente(r.id,"nome",e.target.value)}/>
          <input className="input" placeholder="Ruolo" value={r.ruolo} onChange={e=>updateReferente(r.id,"ruolo",e.target.value)}/>
          <input className="input" placeholder="Telefono" value={r.telefono} onChange={e=>updateReferente(r.id,"telefono",e.target.value)}/>
          <input className="input" placeholder="Email" value={r.email} onChange={e=>updateReferente(r.id,"email",e.target.value)}/>
          <button type="button" onClick={()=>removeReferente(r.id)}
            style={{background:"#fee2e2", border:"none", borderRadius:6, width:32, height:32, cursor:"pointer", color:"#dc2626", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0}}>
            <X size={14}/>
          </button>
        </div>
      ))}
      <button type="button" onClick={addReferente}
        style={{display:"flex", alignItems:"center", gap:6, background:"none", border:"1px dashed #d1d5db", borderRadius:8, padding:"8px 14px", cursor:"pointer", color:"#7c3aed", fontSize:12, fontWeight:600, fontFamily:"'Inter',sans-serif"}}>
        <Plus size={13}/> Aggiungi referente
      </button>
    </div>
  );
}

// ─── Tag liberi (chips) ─────────────────────────────────────────────────
function TagInput({ tags, onChange }) {
  const [input, setInput] = useState("");
  const list = tags || [];

  const addTag = () => {
    const val = input.trim();
    if (val && !list.includes(val)) onChange([...list, val]);
    setInput("");
  };
  const removeTag = tag => onChange(list.filter(t => t !== tag));

  return (
    <div style={{gridColumn:"1/-1"}}>
      <label className="form-label">🏷️ Tag cliente</label>
      <div style={{display:"flex", flexWrap:"wrap", gap:6, marginBottom:8}}>
        {list.map(tag => (
          <span key={tag} style={{display:"flex", alignItems:"center", gap:5, background:"#ede9fe", color:"#7c3aed", padding:"4px 10px", borderRadius:14, fontSize:12, fontWeight:600}}>
            {tag}
            <X size={11} style={{cursor:"pointer"}} onClick={()=>removeTag(tag)}/>
          </span>
        ))}
      </div>
      <div style={{display:"flex", gap:8}}>
        <input className="input" value={input} onChange={e=>setInput(e.target.value)}
          onKeyDown={e=>{ if(e.key==="Enter"){ e.preventDefault(); addTag(); } }}
          placeholder="Es. Premium, Cliente storico, Partner..."/>
        <button type="button" onClick={addTag}
          style={{padding:"0 16px", background:"#7c3aed", color:"#fff", border:"none", borderRadius:8, fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"'Inter',sans-serif", flexShrink:0}}>
          Aggiungi
        </button>
      </div>
    </div>
  );
}

// ─── Documenti (link esterni: Drive, Dropbox, ecc.) ────────────────────
// Fase 1: solo riferimenti/link esterni, nessun upload reale.
// In futuro estendibile con upload su Firebase Storage mantenendo
// la stessa struttura dati (basta aggiungere un campo "fileUrl" interno).
// Ott 2026: tolte le sezioni «Documenti (link esterni)» e «Scadenze» della scheda cliente.
// Le scadenze libere non mandavano promemoria (ora si usano Scadenzario, contratti di
// manutenzione e Pratiche); i documenti proposti erano di subappaltatori e sicurezza (ora
// nelle loro sezioni), compresi dati sanitari che non vanno nell'anagrafica clienti.

function ClienteModal({ cliente, orgId, onClose, onSaved, apriSu }) {
  const { addToast } = useToast();
  const authCli = useAuth();
  const { isReadOnly } = authCli;
  // Sanitizza i valori del cliente esistente: i campi null/undefined salvati
  // in Firestore (es. da findOrCreateCliente, che salva null per opzionali
  // non compilati) vengono convertiti in stringa vuota per evitare crash
  // su .trim() e per mantenere gli input controllati correttamente.
  const sanitized = {};
  if (cliente) Object.keys(cliente).forEach(k => { sanitized[k] = cliente[k] ?? ""; });

  const [form, setForm] = useState({
    ragioneSociale: "", referente: "", email: "", telefono: "",
    indirizzo: "", piva: "", codiceFiscale: "", settore: "", stato: "Attivo", note: "",
    referenti: [], tags: [], documenti: [], scadenze: [], gruppoAziendale: "",
    appaltante: "", appaltatore: "", direzioneLavori: "", rspp: "", referenteTecnico: "",
    ...sanitized
  });
  const [saving, setSaving] = useState(false);
  // Indirizzo della sede legale a pezzi (W438): via, civico, CAP, comune, provincia. Per i
  // clienti salvati prima si prova a dividere la vecchia riga unica, da controllare.
  const [parti, setParti] = useState(() => cliente?.indirizzoParti || scomponiIndirizzo(cliente?.indirizzo));
  const [erroriInd, setErroriInd] = useState({});
  // Aperta da «Aggiungi o modifica sedi» della scheda: si va subito alle sedi
  const sediRef = React.useRef(null);
  useEffect(() => { if (apriSu === "sedi") setTimeout(() => sediRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 150); }, [apriSu]);

  const handle = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async e => {
    e.preventDefault();
    if (bloccaSeReadOnly(isReadOnly, addToast)) return;
    if (!(form.ragioneSociale||"").trim()) { addToast("Ragione sociale obbligatoria", "error"); return; }
    if (!(form.referente||"").trim()) { addToast("Referente obbligatorio", "error"); return; }
    if (!(form.email||"").trim()) { addToast("Email obbligatoria", "error"); return; }

    // Indirizzo completo: senza CAP e civico mappe e navigatori portano da un'altra parte
    const errInd = erroriIndirizzo(parti);
    setErroriInd(errInd);
    if (Object.keys(errInd).length) { addToast("Completa l'indirizzo della sede legale: via, numero civico, CAP, comune e provincia", "error"); return; }

    // Obbligatorio: P.IVA o Codice Fiscale (almeno uno)
    const pivaOk = (form.piva||"").trim().length > 0;
    const cfOk = (form.codiceFiscale||"").trim().length > 0;
    if (!pivaOk && !cfOk) {
      addToast("Obbligatorio inserire P.IVA o Codice Fiscale", "error");
      return;
    }

    // Controllo duplicati P.IVA
    if (form.piva?.trim()) {
      const pivaClean = form.piva.trim().replace(/\s/g,"");
      const snap = await getDocs(query(collection(db,"clienti"), where("organizationId","==",orgId), where("piva","==",pivaClean)));
      const dup = snap.docs.find(d => d.id !== cliente?.id);
      if (dup) { addToast(`P.IVA già presente: ${dup.data().ragioneSociale}`,"error"); return; }
    }
    // Controllo duplicati Codice Fiscale
    if (form.codiceFiscale?.trim()) {
      const cfClean = form.codiceFiscale.trim().toUpperCase();
      const snap = await getDocs(query(collection(db,"clienti"), where("organizationId","==",orgId), where("codiceFiscale","==",cfClean)));
      const dup = snap.docs.find(d => d.id !== cliente?.id);
      if (dup) { addToast(`Codice Fiscale già presente: ${dup.data().ragioneSociale}`,"error"); return; }
    }

    setSaving(true);
    try {
      const partiPulite = Object.fromEntries(Object.entries(parti).map(([k, x]) => [k, String(x || "").replace(/\s+/g, " ").trim()]));
      partiPulite.provincia = partiPulite.provincia.toUpperCase();
      const data = { ...form, indirizzoParti: partiPulite, indirizzo: componiIndirizzo(partiPulite), organizationId: orgId, updatedAt: serverTimestamp() };
      // Sedi: si salvano solo se chi salva può gestirle e le ha davvero cambiate (le sedi
      // aggiunte nel frattempo da un collega dalla commessa o dal rapportino non si perdono);
      // le righe lasciate completamente vuote non si salvano.
      const sediPulite = (Array.isArray(form.sedi) ? form.sedi : [])
        .filter(sd => sd && [sd.nome, sd.indirizzo, sd.comune, sd.referente, sd.telefono].some(x => String(x || "").trim()));
      const sediOriginali = Array.isArray(cliente?.sedi) ? cliente.sedi : [];
      if (puoGestireSedi(authCli) && JSON.stringify(sediPulite) !== JSON.stringify(sediOriginali)) {
        // sedi aggiunte da altri mentre la scheda era aperta: si tengono
        let aggiunteDaAltri = [];
        if (cliente?.id) {
          const fresco = await getDoc(doc(db, "clienti", cliente.id));
          const idNoti = new Set(sediOriginali.map(sd => sd?.id));
          aggiunteDaAltri = (fresco.exists() && Array.isArray(fresco.data().sedi) ? fresco.data().sedi : []).filter(sd => sd?.id && !idNoti.has(sd.id));
        }
        data.sedi = [...sediPulite, ...aggiunteDaAltri];
      } else delete data.sedi;
      if (cliente?.id) {
        await updateDoc(doc(db, "clienti", cliente.id), data);
        addToast("Cliente aggiornato!", "success");
      } else {
        // Numero cliente progressivo, unico per organizzazione — parte da 100 e sale di 1
        // a ogni nuovo cliente creato. Formato coerente con le altre sigle di Nexiora
        // (RC-2026-0001, SOP-2026-0001...), ma senza anno: un cliente non si azzera ogni
        // gennaio come fa un documento. I clienti già esistenti non lo ricevono retroattivamente.
        const progressivo = await prossimoProgressivo(`${orgId}_numeroCliente`, { orgId }, 99);
        const numeroCliente = `CLI-${String(progressivo).padStart(4,"0")}`;
        await addDoc(collection(db, "clienti"), { ...data, numeroCliente, createdAt: serverTimestamp() });
        addToast(`Cliente creato! N° cliente: ${numeroCliente}`, "success");
      }
      onClose();
    } catch(err) { addToast(messaggioErrore(err), "error"); }
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && onClose()}>
      <div className="modal" style={{maxWidth:660, maxHeight:"92vh", overflowY:"auto", padding:16}}>
        <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12}}>
          <h3 style={{fontSize:16, fontWeight:700}}>{cliente?.id ? "Modifica cliente" : "Nuovo cliente"}</h3>
          <button className="btn-icon" onClick={onClose}><X size={16}/></button>
        </div>
        <form onSubmit={submit}>
          <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:8}}>
            <div style={{gridColumn:"1/-1"}}><ClienteField label="Ragione Sociale / Nome" name="ragioneSociale" required value={form.ragioneSociale} onChange={handle}/></div>
            <ClienteField label="Referente principale" name="referente" required value={form.referente} onChange={handle}/>
            <ClienteField label="Email principale" name="email" type="email" required value={form.email} onChange={handle}/>
            <ClienteField label="Telefono" name="telefono" type="tel" value={form.telefono} onChange={handle}/>
            <ClienteField label="Settore" name="settore" options={SETTORI} value={form.settore} onChange={handle}/>
            <ClienteField label="Partita IVA *°" name="piva" value={form.piva} onChange={handle}/>
            <ClienteField label="Codice Fiscale *°" name="codiceFiscale" value={form.codiceFiscale} onChange={handle}/>
            <div style={{gridColumn:"1/-1",fontSize:11,color:"#6b7280",marginTop:-8}}>*° Obbligatorio almeno uno tra P.IVA e Codice Fiscale. Vengono controllati i duplicati automaticamente.</div>
            <div style={{gridColumn:"1/-1",background:"#f9fafb",border:"1px solid #e5e7eb",borderRadius:10,padding:"10px 12px"}}>
              <CampiIndirizzo titolo="📍 Indirizzo della sede legale" valore={parti} onChange={x=>{ setParti(x); if (Object.keys(erroriInd).length) setErroriInd(erroriIndirizzo(x)); }} errori={erroriInd}/>
              {!cliente?.indirizzoParti && cliente?.indirizzo && !indirizzoCompletoOk(parti) && <div style={{fontSize:11,color:"#6d28d9",marginTop:4}}>💜 Indirizzo salvato prima in una sola riga («{cliente.indirizzo}»): controlla i campi e completa quelli che mancano.</div>}
            </div>
            <ClienteField label="Stato" name="stato" options={STATI_CLIENTE} value={form.stato} onChange={handle}/>
            <div>
              <ClienteField label="Condizioni di pagamento abituali (opz.)" name="condizioniPagamentoAbituali" options={MODELLI_PAGAMENTO} value={form.condizioniPagamentoAbituali||""} onChange={handle}/>
              <div style={{fontSize:10,color:"#9ca3af",marginTop:2}}>Verrà proposta di default in Elaborazione Offerte per questo cliente, e comparirà un avviso se viene usata una condizione diversa.</div>
            </div>

            {/* Sedi e siti del cliente (components/SediCliente.js): subito dopo i dati principali,
                in un riquadro ben visibile — prima erano in fondo alla finestra e non si trovavano */}
            <div ref={sediRef} style={{gridColumn:"1/-1"}}>
              <SediManager sedi={Array.isArray(form.sedi) ? form.sedi : []} onChange={sd=>setForm(f=>({...f,sedi:sd}))}/>
            </div>

            {/* Figure contrattuali — prima delle note */}
            <div style={{gridColumn:"1/-1", marginTop:4}}>
              <div style={{fontSize:11, fontWeight:800, color:"#1e1b4b", marginBottom:4}}>🏗️ Figure contrattuali e tecniche <span style={{fontWeight:400, color:"#9ca3af"}}>(opzionali)</span></div>
              <div style={{fontSize:10, color:"#dc2626", background:"#fef2f2", border:"1px solid #fca5a5", borderRadius:6, padding:"5px 8px", marginBottom:8}}>
                ⚠️ Compila solo se la figura è sempre la stessa per tutte le commesse. Se cambia commessa per commessa, inseriscila nel Giornale di Cantiere.
              </div>
              <div style={{display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8}}>
                <ClienteField label="Appaltante" name="appaltante" value={form.appaltante||""} onChange={handle} placeholder="Es. Comune di Napoli"/>
                <ClienteField label="Appaltatore" name="appaltatore" value={form.appaltatore||""} onChange={handle} placeholder="Es. Bianchi Costruzioni Srl"/>
                <ClienteField label="Direzione Lavori" name="direzioneLavori" value={form.direzioneLavori||""} onChange={handle} placeholder="Es. Ing. Mario Rossi"/>
                <ClienteField label="RSPP" name="rspp" value={form.rspp||""} onChange={handle} placeholder="Es. Geom. Anna Bianchi"/>
                <div style={{gridColumn:"span 2"}}><ClienteField label="Referente Tecnico Cliente" name="referenteTecnico" value={form.referenteTecnico||""} onChange={handle} placeholder="Es. Ing. Luigi Verdi"/></div>
              </div>
            </div>

            <ReferentiManager referenti={form.referenti} onChange={r=>setForm(f=>({...f,referenti:r}))}/>

            <TagInput tags={form.tags} onChange={t=>setForm(f=>({...f,tags:t}))}/>
            <ClienteField label="Gruppo aziendale (opz.)" name="gruppoAziendale" value={form.gruppoAziendale} onChange={handle}/>
            <div style={{gridColumn:"1/-1"}}><ClienteField label="Note" name="note" type="textarea" value={form.note} onChange={handle}/></div>
          </div>
          <div style={{display:"flex", gap:8, justifyContent:"flex-end", marginTop:16}}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annulla</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving?"Salvo...":"Salva cliente"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Scheda cliente ────────────────────────────────────────────────

// Mappa interattiva incorporata via Google Maps Embed API.
// Richiede una API Key di Google Maps (Maps Embed API abilitata).
// Finché la key non è impostata, mostra un bottone "Apri in Maps" come fallback,
// così il componente funziona comunque senza bloccare nulla.
const GOOGLE_MAPS_API_KEY = ""; // ← Paolo: inserisci qui la tua API Key quando pronta

function MappaCliente({ indirizzo }) {
  if (!indirizzo) return null;
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(indirizzo)}`;

  return (
    <div style={{background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, overflow:"hidden", marginBottom:20}}>
      <div style={{padding:"14px 18px", borderBottom:"1px solid #e5e7eb", display:"flex", alignItems:"center", justifyContent:"space-between"}}>
        <h3 style={{fontSize:14, fontWeight:700}}>📍 Posizione</h3>
        <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
          style={{fontSize:12, color:"#7c3aed", fontWeight:600, textDecoration:"none"}}>
          Apri in Maps →
        </a>
      </div>
      {GOOGLE_MAPS_API_KEY ? (
        <iframe
          title="Mappa cliente"
          width="100%" height="220" style={{border:0, display:"block"}}
          loading="lazy"
          src={`https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_API_KEY}&q=${encodeURIComponent(indirizzo)}`}>
        </iframe>
      ) : (
        <div style={{padding:20, display:"flex", alignItems:"center", gap:12}}>
          <span style={{fontSize:24}}>📍</span>
          <div style={{flex:1}}>
            <div style={{fontSize:13, color:"#111827", fontWeight:500}}>{indirizzo}</div>
            <div style={{fontSize:11, color:"#9ca3af", marginTop:2}}>Mappa interattiva non ancora configurata</div>
          </div>
        </div>
      )}
    </div>
  );
}

function StelleFedelta({ stelle }) {
  return (
    <span style={{fontSize:13,letterSpacing:1}}>
      {[1,2,3,4,5].map(i => (
        <span key={i} style={{color: i<=stelle ? "#f59e0b" : "#e5e7eb"}}>★</span>
      ))}
    </span>
  );
}

function LockedCard({ icon, title, value, hint }) {
  return (
    <div style={{background:"#f9fafb",border:"1px solid #e5e7eb",borderRadius:10,padding:14,position:"relative",overflow:"hidden"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
        <span style={{fontSize:12,color:"#9ca3af",fontWeight:600}}>{icon} {title}</span>
        <span style={{fontSize:13}}>🔒</span>
      </div>
      <div style={{fontSize:18,fontWeight:700,color:"#d1d5db",filter:"blur(3px)",userSelect:"none"}}>{value}</div>
      {hint && <div style={{fontSize:10,color:"#9ca3af",marginTop:2}}>{hint}</div>}
    </div>
  );
}

// Vecchia sezione «Scadenze» del cliente: le date già scritte si spostano nello Scadenzario
// (scheda Amministrazione), con promemoria a 30 giorni, e poi si tolgono dal cliente.
function VecchieScadenzeCliente({ cliente }) {
  const { addToast } = useToast();
  const [sposto, setSposto] = useState(false);
  const vecchie = (Array.isArray(cliente?.scadenze) ? cliente.scadenze : []).filter(x => x && (x.nome || x.data));
  if (!vecchie.length) return null;
  const sposta = async () => {
    setSposto(true);
    try {
      for (const [i, x] of vecchie.entries()) {
        await addDoc(collection(db, "scadenzario"), {
          nome: x.nome || "Scadenza", tipo: "amministrazione", organizationId: cliente.organizationId,
          targa: "", descrizione: `Cliente: ${cliente.ragioneSociale || ""}`, dataScadenza: x.data || "",
          ricorrenza: "Nessuna", promemoria: true, promemoriaGiorni: 30,
          assegnatoA: "", assegnatoNome: "", note: "Spostata dalla scheda del cliente", completata: false, ordine: i,
          clienteId: cliente.id, createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
        });
      }
      await updateDoc(doc(db, "clienti", cliente.id), { scadenze: [], updatedAt: serverTimestamp() });
      addToast(`${vecchie.length === 1 ? "Scadenza spostata" : `${vecchie.length} scadenze spostate`} nello Scadenzario (scheda Amministrazione), con promemoria`, "success");
    } catch (e) { addToast(messaggioErrore(e, "spostare le scadenze"), "error"); }
    setSposto(false);
  };
  return (
    <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 12, padding: 16, marginBottom: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: "#92400e", marginBottom: 6 }}>📅 Scadenze da spostare nello Scadenzario</div>
      <div style={{ fontSize: 12, color: "#78350f", marginBottom: 8 }}>
        La sezione «Scadenze» del cliente non esiste più perché non mandava promemoria. Queste date sono state scritte lì:
        spostale nello Scadenzario, dove Nexiora le ricorda per tempo.
      </div>
      {vecchie.map((x, i) => <div key={x.id || i} style={{ fontSize: 12.5, color: "#374151" }}>• {x.nome || "Scadenza"}{x.data ? ` — ${new Date(x.data + "T00:00:00").toLocaleDateString("it-IT")}` : ""}</div>)}
      <button className="btn btn-primary btn-sm" style={{ marginTop: 10 }} onClick={sposta} disabled={sposto}>{sposto ? "Sposto…" : "Sposta nello Scadenzario"}</button>
    </div>
  );
}

function SchedaCliente({ cliente, commesse, onEdit, onModificaSedi, onBack, isAdmin, onNavigate }) {
  const { hasFeature, orgPlan } = useFeatures();
  const { effectiveOrgId } = useOrg();
  const { currentUser } = useAuth();
  const isPro = hasFeature("kpi"); // indicatori e analisi cliente: piano Pro (listino Rev1)
  const statoColor = { Attivo:"#059669", Potenziale:"#2563eb", Inattivo:"#9ca3af", Sospeso:"#d97706" };
  const commesseCliente = commesse.filter(c => c.clienteId === cliente.id && !c.isParent);
  const commesseAttive = commesseCliente.filter(c => c.status === "attivo" || !c.archived);
  const valTotale = commesseCliente.reduce((sum, c) => sum + (parseFloat(c.valoreEconomico)||0), 0);

  const health = isPro ? calcClienteHealthScore(commesseCliente) : null;
  const fedelta = isPro ? calcIndiceFedelta(commesseCliente) : null;
  const insight = isPro ? generateBusinessInsight(cliente, commesseCliente) : null;
  const radar = isPro ? generateRadarCliente(cliente, commesseCliente) : null;
  const dna = isPro ? generateCustomerDNA(cliente, commesseCliente) : null;

  // Ultima comunicazione chat: cerca conversazioni collegate a questo cliente
  const [ultimaComunicazione, setUltimaComunicazione] = useState(null);
  const [storicoEconomico, setStoricoEconomico] = useState(null);
  const [attivitaCommerciale, setAttivitaCommerciale] = useState(null);
  const [attivitaOperativa, setAttivitaOperativa] = useState(null);

  // Carica Segnalazioni Cliente e Rientro Materiali di tutte le commesse di questo cliente —
  // dato già esistente (collection segnalazioniCliente/rientriMateriali), nessuna nuova query pesante
  const [orgInfo, setOrgInfo] = useState(null);
  const { addToast } = useToast();
  useEffect(() => {
    if (!effectiveOrgId) return;
    getDoc(doc(db,"organizations",effectiveOrgId)).then(s=>{if(s.exists())setOrgInfo(s.data());});
  }, [effectiveOrgId]);

  useEffect(() => {
    if (!effectiveOrgId || !isAdmin) return;
    const idsCommesse = commesseCliente.map(c => c.id);
    Promise.all([
      getDocs(query(collection(db,"segnalazioniCliente"), where("orgId","==",effectiveOrgId))),
      getDocs(query(collection(db,"rientriMateriali"), where("organizationId","==",effectiveOrgId))),
    ]).then(([segSnap, rmSnap]) => {
      const segCliente = segSnap.docs.map(d=>({id:d.id,...d.data()}))
        .filter(s => s.clienteId===cliente.id || (s.projectId && idsCommesse.includes(s.projectId)))
        .sort((a,b)=>(b.createdAt?.seconds||0)-(a.createdAt?.seconds||0));
      const rmCliente = rmSnap.docs.map(d=>({id:d.id,...d.data()}))
        .filter(r => r.projectId && idsCommesse.includes(r.projectId))
        .sort((a,b)=>(b.createdAt?.seconds||0)-(a.createdAt?.seconds||0));
      setAttivitaOperativa({
        segTotali: segCliente.length,
        segAperte: segCliente.filter(s=>s.stato!=="chiusa").length,
        segRecenti: segCliente.slice(0,4),
        rmTotali: rmCliente.length,
        rmValoreScalato: rmCliente.reduce((s,r)=>s+(r.valoreNoto?(r.quantita*r.prezzoUnitarioApplicato):0),0),
        rmRecenti: rmCliente.slice(0,4),
      });
    }).catch(err => {
      console.error("Errore caricamento attività operativa cliente:", err);
      setAttivitaOperativa({ segTotali:0, segAperte:0, segRecenti:[], rmTotali:0, rmValoreScalato:0, rmRecenti:[], errore:true });
    });
  }, [effectiveOrgId, isAdmin, cliente.id, commesseCliente.length]);

  // Storico Elaborazioni Offerta per questo cliente — stesso schema di Segnalazioni/Rientro Materiali
  const [storicoOfferte, setStoricoOfferte] = useState(null);
  useEffect(() => {
    if (!effectiveOrgId || !isAdmin) return;
    const idsCommesse = commesseCliente.map(c => c.id);
    getDocs(query(collection(db,"elaborazioniOfferte"), where("orgId","==",effectiveOrgId)))
      .then(snap => {
        const eoCliente = snap.docs.map(d=>({id:d.id,...d.data()}))
          .filter(e => e.clienteId===cliente.id || (e.projectId && idsCommesse.includes(e.projectId)))
          .sort((a,b)=>(b.createdAt?.seconds||0)-(a.createdAt?.seconds||0));
        const conMargine = eoCliente.filter(e=>(parseFloat(e.totaleGenerale)||0)>0);
        const margineMedio = conMargine.length>0
          ? conMargine.reduce((s,e)=>s+((parseFloat(e.totaleMargini)||0)/(parseFloat(e.totaleGenerale)||1)*100),0) / conMargine.length
          : null;
        setStoricoOfferte({
          eoTotali: eoCliente.length,
          eoValoreTotale: eoCliente.reduce((s,e)=>s+(parseFloat(e.totaleGenerale)||0),0),
          eoRecenti: eoCliente.slice(0,4),
          margineMedio,
        });
      }).catch(err => {
        console.error("Errore caricamento storico offerte cliente:", err);
        setStoricoOfferte({ eoTotali:0, eoValoreTotale:0, eoRecenti:[], errore:true });
      });
  }, [effectiveOrgId, isAdmin, cliente.id, commesseCliente.length]);

  // Carica sintesi attività commerciale: Richieste Commerciali (per stato reale), DDT, Richieste di Fatturazione
  useEffect(() => {
    if (!effectiveOrgId || !isAdmin) return;
    const idsCommesse = commesseCliente.map(c => c.id);
    Promise.all([
      getDocs(query(collection(db,"richiesteCommerciali"), where("orgId","==",effectiveOrgId))),
      getDocs(query(collection(db,"ddt"), where("orgId","==",effectiveOrgId))),
      getDocs(query(collection(db,"richiesteFatturazione"), where("organizationId","==",effectiveOrgId))),
    ]).then(([rcSnap, ddtSnap, fattSnap]) => {
      const rcCliente = rcSnap.docs.map(d=>({id:d.id,...d.data()}))
        .filter(r => r.clienteId===cliente.id || (r.projectId && idsCommesse.includes(r.projectId)));
      const ddtCliente = ddtSnap.docs.map(d=>({id:d.id,...d.data()}))
        .filter(d => d.projectId && idsCommesse.includes(d.projectId));
      const fattCliente = fattSnap.docs.map(d=>({id:d.id,...d.data()}))
        .filter(f => f.clienteId===cliente.id || (f.projectId && idsCommesse.includes(f.projectId)));

      setAttivitaCommerciale({
        rcTotali: rcCliente.length,
        rcEmesse: rcCliente.filter(r=>["emessa","bozza","inviata","offerta_emessa"].includes(r.stato)).length,
        rcInLavorazione: rcCliente.filter(r=>["in_lavorazione","presa_in_carico","in_elaborazione"].includes(r.stato)).length,
        rcAnnullate: rcCliente.filter(r=>r.stato==="annullata").length,
        valoreOffertoTotale: rcCliente.reduce((s,r)=>s+(parseFloat(r.totaleOfferta)||0),0),
        ddtTotali: ddtCliente.length,
        richiesteFatturazione: fattCliente.length,
        fatturazionePercMedia: fattCliente.length>0 ? Math.round(fattCliente.reduce((s,f)=>s+(parseFloat(f.percentualeComplessiva)||0),0)/fattCliente.length) : null,
      });
    }).catch(err => {
      console.error("Errore caricamento attività commerciale cliente:", err);
      setAttivitaCommerciale({ rcTotali:0, rcEmesse:0, rcInLavorazione:0, rcAnnullate:0, valoreOffertoTotale:0, ddtTotali:0, richiesteFatturazione:0, fatturazionePercMedia:null, errore:true });
    });
  }, [effectiveOrgId, isAdmin, cliente.id, commesseCliente.length]);

  // Carica dati storico economico per tutte le commesse del cliente
  useEffect(() => {
    if (!effectiveOrgId || !isAdmin) return;
    const ids = commesseCliente.map(c => c.id);
    if (ids.length === 0) { setStoricoEconomico({ totaleCosti:0, totaleRicavi:0, commesseConCE:0 }); return; }
    // Carica movimenti economici e budget per tutte le commesse
    // Stesse regole del Controllo Economico (utils/economiaCommessa.js): i ricavi registrati
    // (es. interventi di manutenzione) sono ricavi, non costi; gli ordini di acquisto contano
    // come costo IVA esclusa. Prima i ricavi finivano tra i costi e gli ordini mancavano.
    Promise.all([
      Promise.all(ids.map(id => getDocs(query(collection(db,"movimentiEconomici"), where("projectId","==",id), where("organizationId","==",effectiveOrgId))))),
      Promise.all(ids.map(id => getDocs(query(collection(db,"budgetCommessa"), where("projectId","==",id), where("organizationId","==",effectiveOrgId))))),
      getDocs(query(collection(db,"ordiniAcquisto"), where("organizationId","==",effectiveOrgId))),
    ]).then(([movSnaps, bSnaps, odaSnap]) => {
      const ordini = odaSnap.docs.map(d=>d.data());
      let totaleCosti = 0, totaleRicavi = 0, commesseConCE = 0;
      ids.forEach((id, i) => {
        const movimenti = movSnaps[i].docs.map(d=>d.data());
        const budget = bSnaps[i].empty ? null : bSnaps[i].docs[0].data();
        const eco = economiaSintetica({ budget, movimenti, ordini, projectId: id });
        if (movimenti.length > 0 || eco.costi > 0) commesseConCE++;
        totaleCosti += eco.costi; totaleRicavi += eco.ricavi;
      });
      setStoricoEconomico({ totaleCosti, totaleRicavi, commesseConCE, margine: totaleRicavi - totaleCosti });
    }).catch(err => console.warn("Storico economico cliente non calcolato:", err));
  }, [effectiveOrgId, isAdmin, commesseCliente.length]);
  useEffect(() => {
    if (!effectiveOrgId || !isPro) return;
    // Prima cerca conversazioni con clienteId esplicito (nuove)
    // Regole 74: le chat le leggono solo i partecipanti — si cercano solo quelle in cui c'è chi guarda
    const q = query(collection(db,"conversations"),
      where("organizationId","==",effectiveOrgId),
      where("members","array-contains",currentUser?.uid || "-"),
      where("clienteId","==",cliente.id));
    const unsub = onSnapshot(q, snap => {
      const lista = snap.docs.map(d=>({id:d.id,...d.data()})).filter(c=>c.lastMessageAt);
      if (lista.length > 0) {
        lista.sort((a,b)=>(b.lastMessageAt?.toMillis?.()||0)-(a.lastMessageAt?.toMillis?.()||0));
        setUltimaComunicazione(lista[0]);
      } else {
        // Fallback: cerca per nome cliente nel nome della conversazione
        getDocs(query(collection(db,"conversations"), where("organizationId","==",effectiveOrgId), where("members","array-contains",currentUser?.uid || "-")))
          .then(s => {
            const nomeLower = (cliente.ragioneSociale||cliente.nome||"").toLowerCase();
            const match = s.docs.map(d=>({id:d.id,...d.data()}))
              .filter(c => c.name?.toLowerCase().includes(nomeLower) && c.lastMessageAt)
              .sort((a,b)=>(b.lastMessageAt?.toMillis?.()||0)-(a.lastMessageAt?.toMillis?.()||0));
            setUltimaComunicazione(match[0] || null);
          });
      }
    });
    return () => unsub();
  }, [cliente.id, effectiveOrgId, isPro, currentUser?.uid]);

  const insightColors = {
    verde:     { bg:"linear-gradient(135deg,#d1fae5,#a7f3d0)", border:"#6ee7b7", text:"#065f46" },
    arancione: { bg:"linear-gradient(135deg,#fff7ed,#fed7aa)", border:"#fdba74", text:"#9a3412" },
    rosso:     { bg:"linear-gradient(135deg,#fee2e2,#fecaca)", border:"#fca5a5", text:"#991b1b" },
    neutro:    { bg:"linear-gradient(135deg,#f3f4f6,#e5e7eb)", border:"#d1d5db", text:"#374151" },
  };

  const stampaPDF = () => {
    const win = window.open("","_blank");
    const logoHtml = orgInfo?.logo ? `<img src="${orgInfo.logo}" style="height:36px;object-fit:contain;"/>` :
      `<div style="font-size:16px;font-weight:900;color:#1e1b4b;">◆ ${orgInfo?.nome||orgInfo?.name||""}</div>`;
    const referentiHtml = (cliente.referenti||[]).map(r => `
      <tr><td>${r.nome||"—"}</td><td>${r.ruolo||"—"}</td><td>${r.telefono||"—"}</td><td>${r.email||"—"}</td></tr>
    `).join("");
    const commesseHtml = commesseCliente.map(c => `
      <tr><td>${c.codiceCommessa?`[${c.codiceCommessa}] `:""}${c.name||"—"}</td><td>${c.statoCommessa==="chiusa"?"Chiusa":c.archived?"Archiviata":"Attiva"}</td></tr>
    `).join("");
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>Scheda Cliente — ${cliente.ragioneSociale}</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box;}
      body{font-family:Arial,sans-serif;font-size:12px;color:#111;padding:15mm 18mm;}
      @media screen{body{max-width:800px;margin:0 auto;background:#fff;box-shadow:0 2px 16px rgba(0,0,0,.08);}}
      @page{size:A4 portrait;}
      @media print{.no-print{display:none;}}
      .hdr{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #1e1b4b;padding-bottom:12px;margin-bottom:16px;}
      h2{font-size:13px;color:#7c3aed;margin:18px 0 8px;text-transform:uppercase;letter-spacing:.04em;border-bottom:1px solid #e5e7eb;padding-bottom:4px;}
      .grid{display:grid;grid-template-columns:1fr 1fr;gap:10px 20px;margin-bottom:6px;}
      .campo{font-size:12px;}
      .lbl{font-size:10px;font-weight:700;color:#9ca3af;text-transform:uppercase;letter-spacing:.03em;}
      table{width:100%;border-collapse:collapse;margin-top:6px;}
      th{background:#1e1b4b;color:#fff;padding:6px 8px;font-size:10px;text-align:left;text-transform:uppercase;}
      td{padding:5px 8px;border-bottom:1px solid #e5e7eb;font-size:11px;}
      .kpi-row{display:flex;gap:10px;margin-top:8px;}
      .kpi{flex:1;background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:10px;text-align:center;}
      .kpi-n{font-size:16px;font-weight:800;color:#1e1b4b;}
      .kpi-l{font-size:9px;color:#6b7280;text-transform:uppercase;margin-top:2px;}
    </style></head><body>
      <div class="hdr">
        <div>${logoHtml}<div style="font-size:10px;color:#6b7280;margin-top:3px;">${orgInfo?.nome||orgInfo?.name||""}</div></div>
        <div style="text-align:right;">
          <div style="font-size:16px;font-weight:800;color:#1e1b4b;">🏢 ${cliente.ragioneSociale}</div>
          <div style="font-size:10px;color:#6b7280;margin-top:2px;">Scheda Cliente generata il ${new Date().toLocaleDateString("it-IT")}</div>
        </div>
      </div>

      <h2>Anagrafica</h2>
      <div class="grid">
        <div class="campo"><div class="lbl">Referente principale</div>${cliente.referente||"—"}</div>
        <div class="campo"><div class="lbl">Settore</div>${cliente.settore||"—"}</div>
        <div class="campo"><div class="lbl">Email</div>${cliente.email||"—"}</div>
        <div class="campo"><div class="lbl">Telefono</div>${cliente.telefono||"—"}</div>
        <div class="campo"><div class="lbl">Indirizzo</div>${cliente.indirizzo||"—"}</div>
        <div class="campo"><div class="lbl">Stato</div>${cliente.stato||"—"}</div>
        <div class="campo"><div class="lbl">P.IVA</div>${cliente.piva||"—"}</div>
        <div class="campo"><div class="lbl">Codice Fiscale</div>${cliente.codiceFiscale||"—"}</div>
      </div>
      ${cliente.note ? `<div class="campo" style="margin-top:8px;"><div class="lbl">Note</div>${cliente.note}</div>` : ""}

      ${(cliente.referenti||[]).length>0 ? `
      <h2>Referenti</h2>
      <table><thead><tr><th>Nome</th><th>Ruolo</th><th>Telefono</th><th>Email</th></tr></thead>
      <tbody>${referentiHtml}</tbody></table>` : ""}

      ${isAdmin && storicoEconomico ? `
      <h2>Andamento Economico Complessivo <span style="font-size:9px;color:#9ca3af;text-transform:none;font-weight:400;">(somma di tutte le commesse del cliente)</span></h2>
      <div class="kpi-row">
        <div class="kpi"><div class="kpi-n">${storicoEconomico.totaleRicavi>0?`€${Math.round(storicoEconomico.totaleRicavi).toLocaleString("it-IT")}`:"—"}</div><div class="kpi-l">Ricavi</div></div>
        <div class="kpi"><div class="kpi-n">€${Math.round(storicoEconomico.totaleCosti).toLocaleString("it-IT")}</div><div class="kpi-l">Costi</div></div>
        <div class="kpi"><div class="kpi-n">${storicoEconomico.totaleRicavi>0?`€${Math.round(storicoEconomico.margine).toLocaleString("it-IT")}`:"—"}</div><div class="kpi-l">Margine</div></div>
      </div>` : ""}

      ${isAdmin && attivitaCommerciale ? `
      <h2>Attività Commerciale</h2>
      <div class="grid">
        <div class="campo"><div class="lbl">Richieste commerciali</div>${attivitaCommerciale.rcTotali} (${attivitaCommerciale.rcEmesse} emesse, ${attivitaCommerciale.rcInLavorazione} in lavorazione)</div>
        <div class="campo"><div class="lbl">Valore offerto totale</div>€${Math.round(attivitaCommerciale.valoreOffertoTotale).toLocaleString("it-IT")}</div>
        <div class="campo"><div class="lbl">DDT emessi</div>${attivitaCommerciale.ddtTotali}</div>
        <div class="campo"><div class="lbl">Richieste di fatturazione</div>${attivitaCommerciale.richiesteFatturazione}</div>
      </div>` : ""}

      <h2>Commesse (${commesseCliente.length})</h2>
      <table><thead><tr><th>Commessa</th><th>Stato</th></tr></thead>
      <tbody>${commesseHtml || `<tr><td colspan="2">Nessuna commessa collegata</td></tr>`}</tbody></table>
    </body></html>`);
    win.document.close();
    setTimeout(() => {
      const btn = win.document.createElement("button");
      btn.textContent = "🖨️ Stampa";
      btn.className = "no-print";
      btn.style.cssText = "position:fixed;top:16px;right:16px;padding:10px 20px;background:#7c3aed;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;font-family:Arial,sans-serif;box-shadow:0 4px 12px rgba(124,58,237,.3);z-index:9999;";
      btn.onclick = () => win.print();
      win.document.body.appendChild(btn);
    }, 50);
  };

  return (
    <div style={{flex:1, overflowY:"auto", padding:24}}>
      <div style={{display:"flex", alignItems:"center", gap:12, marginBottom:20}}>
        <button onClick={onBack} style={{background:"none",border:"none",cursor:"pointer",color:"#7c3aed",fontSize:13,fontWeight:600,display:"flex",alignItems:"center",gap:4}}>
          ← Clienti
        </button>
        <button onClick={stampaPDF}
          style={{display:"flex",alignItems:"center",gap:5,padding:"5px 12px",border:"1px solid #7c3aed",borderRadius:7,background:"#faf5ff",color:"#7c3aed",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
          🖨️ Stampa PDF
        </button>
        <ChevronRight size={14} color="#9ca3af"/>
        <span style={{fontSize:14, fontWeight:600}}>{cliente.ragioneSociale}</span>
        <button onClick={()=>updateDoc(doc(db,"clienti",cliente.id),{preferito:!cliente.preferito})}
          title={cliente.preferito ? "Rimuovi dai preferiti" : "Aggiungi ai preferiti"}
          style={{background:"none", border:"none", cursor:"pointer", padding:0, marginLeft:2}}>
          <span style={{fontSize:16, color: cliente.preferito ? "#f59e0b" : "#d1d5db"}}>{cliente.preferito ? "★" : "☆"}</span>
        </button>
      </div>

      {/* ── AI Business Insight (dinamico, solo Pro) ── */}
      {insight && (
        <div style={{background:insightColors[insight.livello].bg, border:`1px solid ${insightColors[insight.livello].border}`, borderRadius:12, padding:"14px 18px", marginBottom:16, display:"flex", gap:10, alignItems:"flex-start"}}>
          <span style={{fontSize:20, flexShrink:0}}>{insight.emoji}</span>
          <div>
            <div style={{fontSize:11, fontWeight:700, color:insightColors[insight.livello].text, textTransform:"uppercase", letterSpacing:"0.04em", marginBottom:3}}>Insight</div>
            <p style={{fontSize:13, color:insightColors[insight.livello].text, lineHeight:1.6, margin:0}}>{insight.testo}</p>
          </div>
        </div>
      )}

      {/* ══ SEZIONE: Sintesi Cliente — DNA + Radar uniti, nessun numero ripetuto altrove ══ */}
      {dna && commesseCliente.length > 0 && (
        <div style={{background:"#fff", border:"2px solid #7c3aed", borderRadius:12, marginBottom:16, overflow:"hidden"}}>
          <div style={{background:"#111827", padding:"18px 20px", color:"#fff"}}>
            <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14}}>
              <div>
                <div style={{fontSize:10, color:"#9ca3af", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:2}}>Sintesi — Customer DNA</div>
                <div style={{fontSize:16, fontWeight:800}}>{dna.nome}</div>
              </div>
              <div style={{fontSize:14}}>
                {[1,2,3,4,5].map(i => <span key={i} style={{color: i<=dna.stelle ? "#f59e0b" : "#4b5563"}}>★</span>)}
              </div>
            </div>
            <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(110px,1fr))", gap:12}}>
              <div><div style={{fontSize:10,color:"#9ca3af"}}>Cliente dal</div><div style={{fontSize:15,fontWeight:700}}>{dna.anno}</div></div>
              <div><div style={{fontSize:10,color:"#9ca3af"}}>Commesse</div><div style={{fontSize:15,fontWeight:700}}>{dna.nCommesse}</div></div>
              <div><div style={{fontSize:10,color:"#9ca3af"}}>Valore totale</div><div style={{fontSize:15,fontWeight:700}}>{isAdmin?`€ ${dna.valoreTotale.toLocaleString("it-IT",{maximumFractionDigits:0})}`:"—"}</div></div>
              <div><div style={{fontSize:10,color:"#9ca3af"}}>Indice fedeltà</div><div style={{fontSize:15,fontWeight:700}}>{dna.indiceFedelta}%</div></div>
              <div><div style={{fontSize:10,color:"#9ca3af"}}>Indice crescita</div><div style={{fontSize:15,fontWeight:700,color:dna.indiceCrescita>0?"#34d399":dna.indiceCrescita<0?"#f87171":"#fff"}}>{dna.indiceCrescita!==null?`${dna.indiceCrescita>0?"+":""}${dna.indiceCrescita}%`:"—"}</div></div>
              <div><div style={{fontSize:10,color:"#9ca3af"}}>Indice puntualità</div><div style={{fontSize:15,fontWeight:700}}>{dna.indicePuntualita!==null?`${dna.indicePuntualita}%`:"—"}</div></div>
              <div><div style={{fontSize:10,color:"#9ca3af"}}>Salute Cliente</div><div style={{fontSize:15,fontWeight:700,color:dna.healthColor}}>{dna.healthEmoji} {dna.healthScore}</div></div>
            </div>
          </div>

          {radar && (
            <div style={{padding:18}}>
              <div style={{display:"flex", alignItems:"center", gap:8, marginBottom:14}}>
                <span style={{fontSize:16}}>{radar.semaforo==="verde"?"🟢":radar.semaforo==="giallo"?"🟡":radar.semaforo==="rosso"?"🔴":"⚪"}</span>
                <h3 style={{fontSize:13, fontWeight:700}}>Radar Cliente</h3>
              </div>
              <div style={{display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14}}>
                <div>
                  <div style={{fontSize:11, fontWeight:700, color:"#059669", marginBottom:6}}>✓ Punti di forza</div>
                  {radar.puntiForza.length===0
                    ? <div style={{fontSize:12, color:"#d1d5db"}}>Nessuno rilevato</div>
                    : radar.puntiForza.map((p,i)=><div key={i} style={{fontSize:12, color:"#374151", marginBottom:4, lineHeight:1.4}}>• {p}</div>)}
                </div>
                <div>
                  <div style={{fontSize:11, fontWeight:700, color:"#dc2626", marginBottom:6}}>⚠ Rischi</div>
                  {radar.rischi.length===0
                    ? <div style={{fontSize:12, color:"#d1d5db"}}>Nessuno rilevato</div>
                    : radar.rischi.map((r,i)=><div key={i} style={{fontSize:12, color:"#374151", marginBottom:4, lineHeight:1.4}}>• {r}</div>)}
                </div>
                <div>
                  <div style={{fontSize:11, fontWeight:700, color:"#2563eb", marginBottom:6}}>💡 Opportunità</div>
                  {radar.opportunita.length===0
                    ? <div style={{fontSize:12, color:"#d1d5db"}}>Nessuna rilevata</div>
                    : radar.opportunita.map((o,i)=><div key={i} style={{fontSize:12, color:"#374151", marginBottom:4, lineHeight:1.4}}>• {o}</div>)}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {(() => { const mostraEconomico = isAdmin && storicoEconomico; return (
      <div style={{display:"grid", gridTemplateColumns: mostraEconomico ? "1fr 1fr" : "1fr", gap:16, marginBottom:16}}>
      {/* ══ SEZIONE: Anagrafica — Informazioni + Note, compatta ══ */}
      <div style={{background:"#fff", border:"2px solid #7c3aed", borderRadius:12, padding:20}}>
        <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14}}>
          <h3 style={{fontSize:14, fontWeight:700}}>📄 Anagrafica</h3>
          <button onClick={onEdit} className="btn btn-secondary btn-sm"><Edit2 size={12}/> Modifica</button>
        </div>
        <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))", gap:14}}>
          {[
            {icon:Building2, label:"Ragione Sociale", val:cliente.ragioneSociale},
            {icon:User, label:"👥 Referente", val:cliente.referente},
            {icon:Mail, label:"Email", val:cliente.email},
            {icon:Phone, label:"📎 Telefono", val:cliente.telefono},
            {icon:MapPin, label:"Indirizzo", val:cliente.indirizzo},
          ].filter(r=>r.val).concat(indirizzoCompletoOk(cliente.indirizzoParti||{}) ? [] : [{icon:MapPin, label:"⚠️ Indirizzo da completare", val:"Mancano via, numero civico, CAP, comune o provincia: con l'indirizzo incompleto mappe e navigatori possono portare da un'altra parte. Apri «Modifica» e completalo."}]).map(r=>(
            <div key={r.label} style={{display:"flex", gap:10, alignItems:"flex-start"}}>
              <r.icon size={14} color="#9ca3af" style={{marginTop:2, flexShrink:0}}/>
              <div>
                <div style={{fontSize:11, color:"#9ca3af"}}>{r.label}</div>
                <div style={{fontSize:13, color:"#111827"}}>{r.val}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{display:"flex", alignItems:"center", gap:8, marginTop:12, flexWrap:"wrap"}}>
          <span style={{fontSize:12, fontWeight:700, color: statoColor[cliente.stato]||"#9ca3af",
            background:(statoColor[cliente.stato]||"#9ca3af")+"22",
            padding:"2px 8px", borderRadius:12}}>{cliente.stato}</span>
          {cliente.settore && <span style={{fontSize:12, color:"#fff", background:"#0891b2", padding:"2px 8px", borderRadius:12, fontWeight:600}}>{cliente.settore}</span>}
          {cliente.tags && cliente.tags.map(tag => (
            <span key={tag} style={{fontSize:11, background:"#ede9fe", color:"#7c3aed", padding:"3px 9px", borderRadius:12, fontWeight:600}}>{tag}</span>
          ))}
        </div>
        {cliente.note && (
          <div style={{background:"#fffbeb", border:"1px solid #fde68a", borderRadius:10, padding:"10px 14px", marginTop:14}}>
            <div style={{fontSize:11, color:"#92400e", fontWeight:600, marginBottom:3}}>Note</div>
            <div style={{fontSize:13, color:"#374151"}}>{cliente.note}</div>
          </div>
        )}
      </div>

      {/* ══ SEZIONE: Andamento Economico ══ */}
      {isAdmin && storicoEconomico && (
        <div style={{background:"#fff", border:"2px solid #7c3aed", borderRadius:12, padding:20}}>
          <h3 style={{fontSize:14, fontWeight:700, marginBottom:2}}>📊 Andamento Economico Complessivo</h3>
          <p style={{fontSize:11, color:"#9ca3af", marginBottom:12}}>Somma di tutte le commesse di questo cliente, non di una singola</p>
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))", gap:10}}>
            <div style={{background:"#f0fdf4", borderRadius:10, padding:"12px 14px"}}>
              <div style={{fontSize:10, color:"#059669", fontWeight:700, marginBottom:4}}>TOTALE RICAVI</div>
              <div style={{fontSize:18, fontWeight:800, color:"#065f46"}}>
                {storicoEconomico.totaleRicavi > 0 ? `€ ${Math.round(storicoEconomico.totaleRicavi).toLocaleString("it-IT")}` : "—"}
              </div>
            </div>
            <div style={{background:"#fef2f2", borderRadius:10, padding:"12px 14px"}}>
              <div style={{fontSize:10, color:"#dc2626", fontWeight:700, marginBottom:4}}>TOTALE COSTI</div>
              <div style={{fontSize:18, fontWeight:800, color:"#991b1b"}}>
                € {Math.round(storicoEconomico.totaleCosti).toLocaleString("it-IT")}
              </div>
            </div>
            <div style={{background: storicoEconomico.margine >= 0 ? "#eff6ff" : "#fef2f2", borderRadius:10, padding:"12px 14px"}}>
              <div style={{fontSize:10, color: storicoEconomico.margine >= 0 ? "#2563eb" : "#dc2626", fontWeight:700, marginBottom:4}}>MARGINE</div>
              <div style={{fontSize:18, fontWeight:800, color: storicoEconomico.margine >= 0 ? "#1d4ed8" : "#991b1b"}}>
                {storicoEconomico.totaleRicavi > 0 ? `€ ${Math.round(storicoEconomico.margine).toLocaleString("it-IT")}` : "—"}
              </div>
              {storicoEconomico.totaleRicavi > 0 && <div style={{fontSize:10,color:"#9ca3af",marginTop:2}}>{Math.round(storicoEconomico.margine/storicoEconomico.totaleRicavi*100)}%</div>}
            </div>
            <div style={{background:"#faf5ff", borderRadius:10, padding:"12px 14px"}}>
              <div style={{fontSize:10, color:"#7c3aed", fontWeight:700, marginBottom:4}}>COMMESSE ATTIVE</div>
              <div style={{fontSize:18, fontWeight:800, color:"#5b21b6"}}>{commesseAttive.length}<span style={{fontSize:12,color:"#9ca3af"}}>/{commesseCliente.length}</span></div>
              <div style={{fontSize:10,color:"#9ca3af",marginTop:2}}>{storicoEconomico.commesseConCE} con CE</div>
            </div>
          </div>
          {storicoEconomico.totaleRicavi === 0 && (
            <div style={{marginTop:8, fontSize:11, color:"#9ca3af", fontStyle:"italic"}}>
              ℹ️ Inserisci il valore contrattuale nel CE di ogni commessa per vedere ricavi e margine.
            </div>
          )}
        </div>
      )}
      </div>
      ); })()}

      {(() => { const mostraFigure = cliente.appaltante || cliente.appaltatore || cliente.direzioneLavori || cliente.rspp || cliente.referenteTecnico;
                const mostraAttivita = isAdmin && attivitaCommerciale;
                return (
      <div style={{display:"grid", gridTemplateColumns: (mostraFigure && mostraAttivita) ? "1fr 1fr" : "1fr", gap:16, marginBottom:16}}>
      {/* ── Sedi e siti del cliente: subito dopo l'anagrafica, con il pulsante per aggiungerle ── */}
      <SediScheda cliente={cliente} orgId={cliente.organizationId} onModifica={onModificaSedi}/>

      {/* ══ SEZIONE: Attività Commerciale Cliente (RC, DDT, Fatturazione) ══ */}
      {isAdmin && attivitaCommerciale && (
        <div style={{background:"#fff", border:"2px solid #7c3aed", borderRadius:12, padding:20}}>
          <h3 style={{fontSize:14, fontWeight:700, marginBottom:14}}>💼 Attività Commerciale</h3>
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))", gap:10}}>
            <div style={{background:"#eff6ff", borderRadius:10, padding:"12px 14px"}}>
              <div style={{fontSize:10, color:"#2563eb", fontWeight:700, marginBottom:4}}>RICHIESTE COMMERCIALI</div>
              <div style={{fontSize:18, fontWeight:800, color:"#1d4ed8"}}>{attivitaCommerciale.rcTotali}</div>
              {attivitaCommerciale.valoreOffertoTotale>0 && <div style={{fontSize:10,color:"#9ca3af",marginTop:2}}>€ {Math.round(attivitaCommerciale.valoreOffertoTotale).toLocaleString("it-IT")} offerti</div>}
            </div>
            <div style={{background:"#f0fdf4", borderRadius:10, padding:"12px 14px"}}>
              <div style={{fontSize:10, color:"#059669", fontWeight:700, marginBottom:4}}>PER STATO</div>
              <div style={{fontSize:11, color:"#065f46", lineHeight:1.6}}>
                <div>🔵 Emesse: <strong>{attivitaCommerciale.rcEmesse}</strong></div>
                <div>🟡 In lavorazione: <strong>{attivitaCommerciale.rcInLavorazione}</strong></div>
                <div>⚪ Annullate: <strong>{attivitaCommerciale.rcAnnullate}</strong></div>
              </div>
            </div>
            <div style={{background:"#faf5ff", borderRadius:10, padding:"12px 14px"}}>
              <div style={{fontSize:10, color:"#7c3aed", fontWeight:700, marginBottom:4}}>DDT</div>
              <div style={{fontSize:18, fontWeight:800, color:"#5b21b6"}}>{attivitaCommerciale.ddtTotali}</div>
            </div>
            <div style={{background:"#fff7ed", borderRadius:10, padding:"12px 14px"}}>
              <div style={{fontSize:10, color:"#c2410c", fontWeight:700, marginBottom:4}}>FATTURAZIONE</div>
              <div style={{fontSize:18, fontWeight:800, color:"#9a3412"}}>{attivitaCommerciale.richiesteFatturazione}</div>
              {attivitaCommerciale.fatturazionePercMedia!==null && <div style={{fontSize:10,color:"#9ca3af",marginTop:2}}>media {attivitaCommerciale.fatturazionePercMedia}% fatturato</div>}
            </div>
          </div>
          {attivitaCommerciale.errore && (
            <div style={{marginTop:8, fontSize:11, color:"#dc2626", background:"#fef2f2", padding:"6px 10px", borderRadius:6}}>
              ⚠️ Errore nel caricamento di questi dati — riprova più tardi o segnalalo se persiste.
            </div>
          )}
          {!attivitaCommerciale.errore && attivitaCommerciale.rcTotali===0 && (
            <div style={{marginTop:8, fontSize:11, color:"#9ca3af", fontStyle:"italic"}}>
              ℹ️ Nessuna richiesta commerciale ancora registrata per questo cliente.
            </div>
          )}
        </div>
      )}

      {/* ── Figure contrattuali ── */}
      {(cliente.appaltante || cliente.appaltatore || cliente.direzioneLavori || cliente.rspp || cliente.referenteTecnico) && (
        <div style={{background:"#fff", border:"2px solid #7c3aed", borderRadius:12, padding:20}}>
          <h3 style={{fontSize:14, fontWeight:700, marginBottom:12}}>🏗️ Figure contrattuali e tecniche</h3>
          <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:10}}>
            {[
              {l:"Appaltante", v:cliente.appaltante, icon:"🏛️"},
              {l:"Appaltatore", v:cliente.appaltatore, icon:"🔧"},
              {l:"Direzione Lavori", v:cliente.direzioneLavori, icon:"📐"},
              {l:"RSPP", v:cliente.rspp, icon:"🦺"},
              {l:"Referente Tecnico", v:cliente.referenteTecnico, icon:"👷"},
            ].filter(f=>f.v).map(f=>(
              <div key={f.l} style={{background:"#f9fafb", borderRadius:8, padding:"8px 12px"}}>
                <div style={{fontSize:10, color:"#9ca3af", fontWeight:600, marginBottom:2}}>{f.icon} {f.l.toUpperCase()}</div>
                <div style={{fontSize:13, fontWeight:600, color:"#111827"}}>{f.v}</div>
              </div>
            ))}
          </div>
          <div style={{fontSize:10, color:"#f59e0b", marginTop:8, fontStyle:"italic"}}>
            ⚠️ Figure valide per tutte le commesse di questo cliente. Se cambiano per commessa, usare il Giornale di Cantiere.
          </div>
        </div>
      )}
      </div>
      ); })()}

      {(() => { const mostraReferenti = cliente.referenti && cliente.referenti.filter(r=>r.nome).length > 0;
                const mostraGruppo = !!cliente.gruppoAziendale;
                return (
      <div style={{display:"grid", gridTemplateColumns: (mostraReferenti && mostraGruppo) ? "1fr 1fr" : "1fr", gap:16, marginBottom:20}}>
      {/* ── Referenti multipli (sempre visibile, Free incluso) ── */}
      {cliente.referenti && cliente.referenti.filter(r=>r.nome).length > 0 && (
        <div style={{background:"#fff", border:"2px solid #7c3aed", borderRadius:12, padding:20}}>
          <h3 style={{fontSize:14, fontWeight:700, marginBottom:14}}>👥 Referenti</h3>
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))", gap:14}}>
            {cliente.referenti.filter(r=>r.nome).map(r => (
              <div key={r.id} style={{background:"#f9fafb", borderRadius:10, padding:14}}>
                <div style={{fontSize:14, fontWeight:700, color:"#111827"}}>{r.nome}</div>
                {r.ruolo && <div style={{fontSize:12, color:"#6b7280", marginBottom:8}}>{r.ruolo}</div>}
                <div style={{display:"flex", flexDirection:"column", gap:4}}>
                  {r.telefono && <div style={{fontSize:12, color:"#374151", display:"flex", alignItems:"center", gap:6}}><Phone size={11} color="#9ca3af"/>{r.telefono}</div>}
                  {r.email && <div style={{fontSize:12, color:"#374151", display:"flex", alignItems:"center", gap:6}}><Mail size={11} color="#9ca3af"/>{r.email}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Gruppo aziendale: collegamenti (sempre visibile, Free incluso) ── */}
      {cliente.gruppoAziendale && (
        <div style={{background:"#fff", border:"2px solid #7c3aed", borderRadius:12, padding:20}}>
          <h3 style={{fontSize:14, fontWeight:700, marginBottom:10}}>🔗 Gruppo aziendale</h3>
          <div style={{display:"flex", alignItems:"center", gap:8}}>
            <span style={{fontSize:13, fontWeight:700, color:"#374151", background:"#f3f4f6", padding:"6px 14px", borderRadius:8}}>{cliente.gruppoAziendale}</span>
            <span style={{color:"#9ca3af"}}>↓</span>
            <span style={{fontSize:13, fontWeight:700, color:"#7c3aed", background:"#ede9fe", padding:"6px 14px", borderRadius:8}}>{cliente.ragioneSociale}</span>
          </div>
          <div style={{fontSize:11, color:"#9ca3af", marginTop:8}}>Altri clienti dello stesso gruppo appariranno qui quando associati</div>
        </div>
      )}
      </div>
      ); })()}

      {/* ── Mappa (sempre visibile se c'è indirizzo, Free incluso) ── */}
      <MappaCliente indirizzo={cliente.indirizzo}/>


      {/* Date scritte nella vecchia sezione «Scadenze» (tolta ott 2026): si spostano nello
          Scadenzario con un clic, dove diventano promemoria veri */}
      <VecchieScadenzeCliente cliente={cliente}/>

      {(() => { const mostraUltimaCom = isPro; return (
      <div style={{display:"grid", gridTemplateColumns: mostraUltimaCom ? "1fr 1fr" : "1fr", gap:16, marginBottom:20}}>
      {/* ── Ultima comunicazione (solo Pro) ── */}
      {isPro && (
        <div style={{background:"#fff", border:"2px solid #7c3aed", borderRadius:12, padding:18}}>
          <div style={{display:"flex", alignItems:"center", gap:8, marginBottom: ultimaComunicazione ? 12 : 0}}>
            <MessageSquare size={15} color="#7c3aed"/>
            <h3 style={{fontSize:14, fontWeight:700}}>Ultima comunicazione</h3>
          </div>
          {ultimaComunicazione ? (
            <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", background:"#f9fafb", borderRadius:8, padding:"10px 14px"}}>
              <div>
                <div style={{fontSize:13, fontWeight:600, color:"#111827"}}>{ultimaComunicazione.name}</div>
                <div style={{fontSize:11, color:"#9ca3af", marginTop:2}}>
                  {ultimaComunicazione.lastMessage && <span>"{ultimaComunicazione.lastMessage.substring(0,60)}{ultimaComunicazione.lastMessage.length>60?"...":""}"</span>}
                </div>
              </div>
              <div style={{fontSize:12, fontWeight:600, color:"#7c3aed", textAlign:"right", flexShrink:0, marginLeft:12}}>
                {ultimaComunicazione.lastMessageAt?.toDate?.()?.toLocaleDateString("it-IT",{day:"numeric",month:"long"})}
              </div>
            </div>
          ) : (
            <div style={{fontSize:13, color:"#9ca3af"}}>Nessuna comunicazione chat collegata a questo cliente.</div>
          )}
        </div>
      )}

      {/* ── Schede avanzate Pro / Lucchetti Free ── */}
      <div style={{background:"#fff", border:"2px solid #7c3aed", borderRadius:12, padding:20}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
          <h3 style={{fontSize:14,fontWeight:700}}>📊 Statistiche Aggiuntive</h3>
          {isPro && fedelta && <StelleFedelta stelle={fedelta.stelle}/>}
        </div>

        {isPro ? (
          health && commesseCliente.length > 0 ? (
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))",gap:12}}>
              <div style={{background:"#f9fafb",borderRadius:10,padding:14}}>
                <div style={{fontSize:11,color:"#9ca3af",marginBottom:4}}>Tempo medio chiusura</div>
                <div style={{fontSize:18,fontWeight:700}}>{health.dettagli.tempoMedioMesi>0?`${health.dettagli.tempoMedioMesi} mesi`:"—"}</div>
              </div>
              <div style={{background:"#f9fafb",borderRadius:10,padding:14}}>
                <div style={{fontSize:11,color:"#9ca3af",marginBottom:4}}>% Completamento</div>
                <div style={{fontSize:18,fontWeight:700}}>{health.dettagli.pctCompletamento}%</div>
              </div>
            </div>
          ) : (
            <div style={{textAlign:"center",padding:20,color:"#9ca3af",fontSize:13}}>Nessuna commessa ancora — le statistiche appariranno alla prima commessa registrata</div>
          )
        ) : (
          <>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))",gap:12,marginBottom:14}}>
              <LockedCard icon="💰" title="Valore economico" value="€ •••••"/>
              <LockedCard icon="⏱️" title="Tempo medio chiusura" value="•• mesi"/>
              <LockedCard icon="📈" title="Trend" value="••%"/>
              <LockedCard icon="🕐" title="Timeline" value="••••••"/>
              <LockedCard icon="❤️" title="Salute Cliente" value="••/100"/>
              <LockedCard icon="🧬" title="Customer DNA" value="••••••"/>
              <LockedCard icon="📡" title="Radar Cliente" value="••••••"/>
              <LockedCard icon="✨" title="AI Business Insight" value="••••••"/>
            </div>
            <div style={{background:"linear-gradient(135deg,#7c3aed,#2563eb)",borderRadius:10,padding:"16px 18px",textAlign:"center"}}>
              <div style={{fontSize:13,color:"#fff",fontWeight:600,marginBottom:8}}>
                Sblocca AI Business Insight, Radar Cliente, Customer DNA e statistiche avanzate
              </div>
              <button style={{padding:"8px 20px",background:"#fff",color:"#7c3aed",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"\'Inter\',sans-serif"}}>
                Passa a Pro →
              </button>
            </div>
          </>
        )}
      </div>
      </div>
      ); })()}

      {(() => { const mostraTimeline = isPro && commesseCliente.length > 0; return (
      <div style={{display:"grid", gridTemplateColumns: mostraTimeline ? "1fr 1fr" : "1fr", gap:16, marginBottom:20}}>
      {/* Timeline cliente — solo Pro */}
      {isPro && commesseCliente.length > 0 && (
        <div style={{background:"#fff", border:"2px solid #7c3aed", borderRadius:12, padding:20}}>
          <h3 style={{fontSize:14, fontWeight:700, marginBottom:16}}>🕐 Timeline</h3>
          <div style={{display:"flex",flexDirection:"column",gap:0}}>
            {[...commesseCliente].sort((a,b)=>(b.createdAt?.toMillis?.()||0)-(a.createdAt?.toMillis?.()||0)).map((c,i,arr)=>(
              <div key={c.id} style={{display:"flex",gap:12}}>
                <div style={{display:"flex",flexDirection:"column",alignItems:"center",flexShrink:0}}>
                  <div style={{width:10,height:10,borderRadius:"50%",background:c.archived?"#9ca3af":"#7c3aed",marginTop:6}}/>
                  {i<arr.length-1 && <div style={{width:2,flex:1,background:"#e5e7eb",minHeight:24}}/>}
                </div>
                <div style={{paddingBottom:16,flex:1}}>
                  {/* Commessa + valore + stato sulla stessa riga */}
                  <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                    <span style={{fontSize:13,fontWeight:700,color:"#111827"}}>{c.name}</span>
                    {c.valoreEconomico > 0 && isAdmin && (
                      <span style={{fontSize:13,fontWeight:700,color:"#059669"}}>
                        € {parseFloat(c.valoreEconomico).toLocaleString("it-IT",{minimumFractionDigits:0})}
                      </span>
                    )}
                    <span style={{fontSize:11,padding:"1px 7px",borderRadius:10,
                      background:c.archived?"#f3f4f6":"#d1fae5",
                      color:c.archived?"#9ca3af":"#059669",fontWeight:600}}>
                      {c.archived?"Archiviata":"Attiva"}
                    </span>
                  </div>
                  <div style={{fontSize:11,color:"#9ca3af",marginTop:2}}>
                    {c.createdAt?.toDate?.()?.toLocaleDateString("it-IT",{day:"numeric",month:"long",year:"numeric"})}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lista commesse (sempre visibile) */}
      <div style={{background:"#fff", border:"2px solid #7c3aed", borderRadius:12, overflow:"hidden"}}>
        <div style={{padding:"14px 18px", borderBottom:"1px solid #e5e7eb", display:"flex", alignItems:"center", justifyContent:"space-between"}}>
          <h3 style={{fontSize:14, fontWeight:700}}>📁 Commesse ({commesseCliente.length})</h3>
        </div>
        {commesseCliente.length===0 ? (
          <div style={{padding:24, textAlign:"center", color:"#9ca3af", fontSize:13}}>Nessuna commessa associata a questo cliente</div>
        ) : commesseCliente.map(c => (
          <div key={c.id} style={{display:"flex", alignItems:"center", gap:12, padding:"12px 18px", borderBottom:"1px solid #f3f4f6"}}>
            <div style={{width:10, height:10, borderRadius:3, background:c.color||"#7c3aed", flexShrink:0}}/>
            <div style={{flex:1}}>
              <div style={{fontSize:13, fontWeight:600}}>
                {c.statoCommessa==="chiusa"&&<span style={{marginRight:5}} title="Commessa chiusa">🔒</span>}
                <span style={{opacity:c.statoCommessa==="chiusa"?0.6:1}}>{c.name}</span>
              </div>
              <div style={{fontSize:11, color:"#9ca3af"}}>
                {c.codiceCommessa&&<span style={{fontFamily:"monospace",marginRight:6,color:"#7c3aed"}}>{c.codiceCommessa}</span>}
                ID: {c.idCommessa||c.id.slice(0,8)}
                {c.statoCommessa==="chiusa"&&c.dataChiusura&&<span style={{marginLeft:6}}>· Chiusa il {new Date(c.dataChiusura+"T00:00:00").toLocaleDateString("it-IT")}</span>}
              </div>
            </div>
            {isAdmin && isPro && c.valoreEconomico && (
              <span style={{fontSize:13, fontWeight:600, color:"#059669"}}>
                € {parseFloat(c.valoreEconomico).toLocaleString("it-IT", {minimumFractionDigits:2})}
              </span>
            )}
            <span style={{fontSize:11, padding:"2px 8px", borderRadius:12,
              background: c.archived?"#f3f4f6":c.statoCommessa==="chiusa"?"#fee2e2":"#d1fae5",
              color: c.archived?"#9ca3af":c.statoCommessa==="chiusa"?"#dc2626":"#059669", fontWeight:600}}>
              {c.archived ? "Archiviata" : c.statoCommessa==="chiusa" ? "Chiusa" : "Attiva"}
            </span>
          </div>
        ))}
      </div>
      </div>
      ); })()}

      {isAdmin && attivitaOperativa && (attivitaOperativa.segTotali>0 || attivitaOperativa.rmTotali>0) && (
      <div style={{display:"grid", gridTemplateColumns: (attivitaOperativa.segTotali>0 && attivitaOperativa.rmTotali>0) ? "1fr 1fr" : "1fr", gap:16, marginBottom:20}}>
        {attivitaOperativa.segTotali>0 && (
          <div style={{background:"#fff", border:"2px solid #7c3aed", borderRadius:12, padding:20}}>
            <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14}}>
              <h3 style={{fontSize:14, fontWeight:700, margin:0}}>📣 Segnalazioni Cliente</h3>
              <span style={{fontSize:11, fontWeight:700, color: attivitaOperativa.segAperte>0?"#d97706":"#059669", background: attivitaOperativa.segAperte>0?"#fffbeb":"#f0fdf4", padding:"2px 9px", borderRadius:10}}>
                {attivitaOperativa.segAperte>0 ? `${attivitaOperativa.segAperte} aperta/e` : "Tutte chiuse"}
              </span>
            </div>
            <div style={{display:"flex", flexDirection:"column", gap:8, marginBottom:12}}>
              {attivitaOperativa.segRecenti.map(s=>(
                <div key={s.id} style={{display:"flex", alignItems:"center", gap:8, fontSize:12}}>
                  <span style={{fontFamily:"monospace", fontWeight:700, color:"#7c3aed", flexShrink:0}}>{s.numero}</span>
                  <span style={{color:"#374151", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1}}>{s.descrizione}</span>
                  <span style={{fontSize:10, color: s.stato==="chiusa"?"#9ca3af":"#d97706", fontWeight:600, flexShrink:0}}>{s.stato==="chiusa"?"Chiusa":s.stato==="in_lavorazione"?"In lav.":"Nuova"}</span>
                </div>
              ))}
            </div>
            <button onClick={()=>onNavigate && onNavigate({page:"registro-segnalazioni", clienteId:cliente.id})}
              style={{fontSize:11, fontWeight:700, color:"#7c3aed", background:"#faf5ff", border:"1px solid #e9d5ff", borderRadius:7, padding:"6px 12px", cursor:"pointer"}}>
              Vedi tutte ({attivitaOperativa.segTotali}) →
            </button>
          </div>
        )}
        {attivitaOperativa.rmTotali>0 && (
          <div style={{background:"#fff", border:"2px solid #7c3aed", borderRadius:12, padding:20}}>
            <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14}}>
              <h3 style={{fontSize:14, fontWeight:700, margin:0}}>🔄 Rientro Materiali</h3>
              {isPro && attivitaOperativa.rmValoreScalato>0 && (
                <span style={{fontSize:11, fontWeight:700, color:"#059669", background:"#f0fdf4", padding:"2px 9px", borderRadius:10}}>
                  -€ {attivitaOperativa.rmValoreScalato.toLocaleString("it-IT",{minimumFractionDigits:0})} scalati
                </span>
              )}
            </div>
            <div style={{display:"flex", flexDirection:"column", gap:8, marginBottom:12}}>
              {attivitaOperativa.rmRecenti.map(r=>(
                <div key={r.id} style={{display:"flex", alignItems:"center", gap:8, fontSize:12}}>
                  <span style={{fontFamily:"monospace", fontWeight:700, color:"#7c3aed", flexShrink:0}}>{r.numero}</span>
                  <span style={{color:"#374151", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1}}>{r.descrizione}</span>
                  <span style={{fontSize:10, color:"#6b7280", flexShrink:0}}>{r.quantita} {r.unitaMisura}</span>
                </div>
              ))}
            </div>
            <button onClick={()=>onNavigate && onNavigate({page:"registro-rientri-materiali", clienteId:cliente.id})}
              style={{fontSize:11, fontWeight:700, color:"#7c3aed", background:"#faf5ff", border:"1px solid #e9d5ff", borderRadius:7, padding:"6px 12px", cursor:"pointer"}}>
              Vedi tutti ({attivitaOperativa.rmTotali}) →
            </button>
          </div>
        )}
      </div>
      )}

      {isAdmin && storicoOfferte && storicoOfferte.eoTotali>0 && (
      <div style={{background:"#fff", border:"2px solid #7c3aed", borderRadius:12, padding:20, marginBottom:20}}>
        <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14}}>
          <h3 style={{fontSize:14, fontWeight:700, margin:0}}>📐 Elaborazioni Offerta</h3>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {isPro && storicoOfferte.eoValoreTotale>0 && (
              <span style={{fontSize:11, fontWeight:700, color:"#059669", background:"#f0fdf4", padding:"2px 9px", borderRadius:10}}>
                € {storicoOfferte.eoValoreTotale.toLocaleString("it-IT",{minimumFractionDigits:0})} elaborati in totale
              </span>
            )}
            {isPro && storicoOfferte.margineMedio!==null && (
              <span style={{fontSize:11, fontWeight:700, color:"#7c3aed", background:"#f5f3ff", padding:"2px 9px", borderRadius:10}}>
                {(storicoOfferte.margineMedio).toLocaleString("it-IT",{minimumFractionDigits:1,maximumFractionDigits:1})}% margine medio
              </span>
            )}
          </div>
        </div>
        <div style={{display:"flex", flexDirection:"column", gap:8, marginBottom:12}}>
          {storicoOfferte.eoRecenti.map(e=>(
            <div key={e.id} style={{display:"flex", alignItems:"center", gap:8, fontSize:12}}>
              <span style={{fontFamily:"monospace", fontWeight:700, color:"#7c3aed", flexShrink:0}}>{e.numero}</span>
              <span style={{color:"#374151", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1}}>{e.oggetto}</span>
              <span style={{fontSize:11, fontWeight:700, color:"#059669", flexShrink:0}}>€ {(parseFloat(e.totaleGenerale)||0).toLocaleString("it-IT",{minimumFractionDigits:0})}</span>
            </div>
          ))}
        </div>
        <button onClick={()=>onNavigate && onNavigate({page:"elaborazione-offerte", clienteId:cliente.id, clienteNome:cliente.ragioneSociale})}
          style={{fontSize:11, fontWeight:700, color:"#7c3aed", background:"#faf5ff", border:"1px solid #e9d5ff", borderRadius:7, padding:"6px 12px", cursor:"pointer"}}>
          Vedi tutte ({storicoOfferte.eoTotali}) →
        </button>
        {storicoOfferte.eoTotali>1 && (
          <div style={{fontSize:10.5, color:"#9ca3af", marginTop:8}}>
            💜 Questo cliente ha già {storicoOfferte.eoTotali} elaborazioni precedenti — utile confrontarle prima di crearne una nuova.
          </div>
        )}
      </div>
      )}
    </div>
  );
}

// ─── Pagina Clienti principale ─────────────────────────────────────
export default function ClientiPage({ deepLink, onNavigate }) {
  const { effectiveOrgId } = useOrg();
  const { isPlatformOwner, isOrgAdmin } = useAuth();
  const { hasFeature, orgPlan, PLANS } = useFeatures();
  const { addToast } = useToast();
  const isAdmin = isPlatformOwner || isOrgAdmin;

  const [clienti, setClienti] = useState([]);
  const [commesseGrezze, setCommesse] = useState([]);
  // W420: il valore di ogni commessa viene dal budget (importo + varianti accettate)
  const valoriCommesse = useValoriCommesse(effectiveOrgId, isAdmin);
  const commesse = useMemo(() => conValore(commesseGrezze, valoriCommesse), [commesseGrezze, valoriCommesse]);
  const [search, setSearch] = useState("");
  const [filterStato, setFilterStato] = useState("tutti");
  const [soloPreferiti, setSoloPreferiti] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [apriSu, setApriSu] = useState(null); // "sedi" = la finestra si apre già sulle sedi
  const [vistaClienti, setVistaClienti] = useState("card");
  const [clienteDaEliminare, setClienteDaEliminare] = useState(null); // "card" | "lista"
  const [editCliente, setEditCliente] = useState(null);
  const [selectedCliente, setSelectedCliente] = useState(deepLink?.clienteId || null);
  // Se arriva un nuovo deepLink, aggiorna la selezione
  React.useEffect(() => { if(deepLink?.clienteId) setSelectedCliente(deepLink.clienteId); }, [deepLink?.clienteId]);

  useEffect(() => {
    if (!effectiveOrgId) return;
    const u1 = onSnapshot(query(collection(db,"clienti"), where("organizationId","==",effectiveOrgId)),
      snap => setClienti(snap.docs.map(d=>({id:d.id,...d.data()})))
    );
    const u2 = onSnapshot(query(collection(db,"projects"), where("organizationId","==",effectiveOrgId)),
      snap => setCommesse(snap.docs.map(d=>({id:d.id,...d.data()})).filter(p=>!p.isParent))
    );
    return () => { u1(); u2(); };
  }, [effectiveOrgId]);

  // Teaser per chi non ha il piano Pro
  if (!hasFeature("centroClienti") && !hasFeature("gestCommesse")) {
    return (
      <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
        <div style={{background:"#fff",borderBottom:"1px solid #e5e7eb",padding:"14px 24px",flexShrink:0}}>
          <h2 style={{fontSize:18,fontWeight:700,display:"flex",alignItems:"center",gap:8}}>
            <span>🏢</span> Clienti & Commesse
          </h2>
            <button onClick={()=>{
              const intestazione=["Ragione Sociale","P.IVA","Indirizzo","Telefono","Email","Referente"];
              const righe=(clienti||[]).map(c=>[ c.ragioneSociale||c.nome||"—",c.piva||"—",c.indirizzo||"—",c.telefono||"—",c.email||"—",c.referente||"—"]);
              esportaXLSX([intestazione,...righe],`Anagrafica_Clienti_${new Date().toLocaleDateString("it-IT").replace(/\//g,"-")}`,"Clienti");
            }} style={{display:"flex",alignItems:"center",gap:5,padding:"6px 12px",border:"1px solid #059669",borderRadius:7,background:"#f0fdf4",color:"#059669",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
              📊 Excel
            </button>
        </div>
        <div style={{flex:1,overflowY:"auto",padding:40,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{maxWidth:560,textAlign:"center"}}>
            {/* Preview illustration */}
            <div style={{background:"linear-gradient(135deg,#7c3aed,#2563eb)",borderRadius:20,padding:32,marginBottom:32,position:"relative",overflow:"hidden"}}>
              <div style={{fontSize:48,marginBottom:12}}>🏢</div>
              <div style={{fontSize:20,fontWeight:800,color:"#fff",marginBottom:8}}>Clienti & Commesse</div>
              <div style={{fontSize:14,color:"rgba(255,255,255,0.8)",lineHeight:1.6}}>
                Il modulo professionale per chi lavora a commessa
              </div>
              {/* Decorative blurred shapes */}
              <div style={{position:"absolute",top:-20,right:-20,width:100,height:100,borderRadius:"50%",background:"rgba(255,255,255,0.1)"}}/>
              <div style={{position:"absolute",bottom:-30,left:-10,width:140,height:140,borderRadius:"50%",background:"rgba(255,255,255,0.05)"}}/>
            </div>

            {/* Feature list */}
            <div style={{textAlign:"left",marginBottom:32}}>
              <div style={{fontSize:16,fontWeight:700,color:"#111827",marginBottom:16}}>Con il piano Pro ottieni:</div>
              {[
                { icon:"👤", title:"Anagrafica Clienti centralizzata", desc:"Gestisci tutti i tuoi clienti in un unico posto. Dati anagrafici, referenti, contatti, storico commesse." },
                { icon:"📁", title:"Gestione Commesse avanzata", desc:"Ogni commessa è collegata al cliente. Tracci apertura, stato e chiusura con un workflow strutturato." },
                { icon:"🔗", title:"Field mapping intelligente", desc:"I campi dati delle attività possono ereditare automaticamente i dati del cliente collegato (referente, email, telefono...)." },
                { icon:"💰", title:"Valori economici per commessa", desc:"Assegna un valore economico a ogni commessa. Visibile solo agli amministratori. KPI economici aggregati per cliente." },
                { icon:"📊", title:"Dashboard KPI per cliente", desc:"Vedi in un colpo d'occhio quante commesse ha ogni cliente, il loro stato e il valore economico totale." },
              ].map((f,i) => (
                <div key={i} style={{display:"flex",gap:14,padding:"14px 0",borderBottom:"1px solid #f3f4f6"}}>
                  <div style={{fontSize:24,flexShrink:0}}>{f.icon}</div>
                  <div>
                    <div style={{fontSize:14,fontWeight:600,color:"#111827",marginBottom:3}}>{f.title}</div>
                    <div style={{fontSize:13,color:"#6b7280",lineHeight:1.5}}>{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Piano attuale + CTA */}
            <div style={{background:"#f9fafb",border:"1px solid #e5e7eb",borderRadius:12,padding:20,marginBottom:20}}>
              <div style={{fontSize:13,color:"#6b7280",marginBottom:4}}>Piano attuale</div>
              <div style={{fontSize:18,fontWeight:700,color:"#111827",marginBottom:12}}>
                {PLANS[orgPlan]?.label || "Free"} — {PLANS[orgPlan]?.price || "Gratuito"}
              </div>
              <div style={{fontSize:13,color:"#374151",lineHeight:1.6,marginBottom:16}}>
                Questa funzionalità è disponibile a partire dal piano <strong style={{color:"#7c3aed"}}>Pro</strong>.
                {orgPlan === "free" && " Con il piano Pro ottieni anche il Centro Operativo con KPI avanzati, alert intelligenti e situazione operativa in tempo reale."}
              </div>
              <div style={{display:"flex",gap:10,justifyContent:"center"}}>
                <button style={{padding:"10px 24px",background:"#7c3aed",color:"#fff",border:"none",borderRadius:10,fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                  Passa al piano Pro →
                </button>
                {isAdmin && (
                  <button style={{padding:"10px 24px",background:"#fff",color:"#374151",border:"1px solid #e5e7eb",borderRadius:10,fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                    Contatta il supporto
                  </button>
                )}
              </div>
            </div>
            <div style={{fontSize:12,color:"#9ca3af"}}>
              Già sul piano Pro? Vai in Impostazioni → Funzionalità e attiva "Clienti & Commesse".
            </div>
          </div>
        </div>
      </div>
    );
  }

  const filtered = clienti.filter(c => {
    if (filterStato!=="tutti" && c.stato!==filterStato) return false;
    if (soloPreferiti && !c.preferito) return false;
    if (search && !c.ragioneSociale?.toLowerCase().includes(search.toLowerCase()) &&
        !c.referente?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }).sort((a,b) => (b.preferito?1:0) - (a.preferito?1:0));

  const statoColor = { Attivo:"#059669", Potenziale:"#2563eb", Inattivo:"#9ca3af", Sospeso:"#d97706" };

  if (selectedCliente) {
    const cliente = clienti.find(c=>c.id===selectedCliente);
    if (!cliente) { setSelectedCliente(null); return null; }
    return (
      <div style={{display:"flex", flexDirection:"column", height:"100%", overflow:"hidden"}}>
        <div style={{background:"#fff", borderBottom:"1px solid #e5e7eb", padding:"12px 24px", flexShrink:0}}>
          <h2 style={{fontSize:18, fontWeight:700}}>Clienti</h2>
        </div>
        <SchedaCliente cliente={cliente} commesse={commesse}
          onEdit={()=>{ setApriSu(null); setEditCliente(cliente); setShowModal(true); }}
          onModificaSedi={()=>{ setApriSu("sedi"); setEditCliente(cliente); setShowModal(true); }}
          onBack={()=>setSelectedCliente(null)} isAdmin={isAdmin} onNavigate={onNavigate}
/>
        {showModal && <ClienteModal cliente={editCliente} orgId={effectiveOrgId} apriSu={apriSu}
          onClose={()=>{ setShowModal(false); setEditCliente(null); setApriSu(null); }}/>}
      </div>
    );
  }

  return (
    <div style={{display:"flex", flexDirection:"column", height:"100%", overflow:"hidden"}}>
      {/* Header */}
      <div style={{background:"#fff", borderBottom:"1px solid #e5e7eb", padding:"14px 24px", flexShrink:0}}>
        <div style={{display:"flex", alignItems:"center", justifyContent:"space-between"}}>
          <div>
            <h2 style={{fontSize:18, fontWeight:700, display:"flex", alignItems:"center", gap:8}}>
              <Building2 size={20} color="#7c3aed"/> Clienti
              {isAdmin && (
                <button className="btn btn-primary" onClick={()=>{ setEditCliente(null); setShowModal(true); }}
                  style={{display:"flex",alignItems:"center",gap:6,fontSize:13,padding:"4px 12px"}}>
                  <Plus size={14}/> Nuovo cliente
                </button>
              )}
              {isAdmin && (
                <ImportaExcelModal
                  label="Importa clienti da Excel"
                  campiMappabili={[
                    {key:"ragioneSociale",label:"Ragione Sociale",indovina:/ragione|denomina|nome\s*cliente|azienda/},
                    {key:"piva",label:"P.IVA",indovina:/p\.?iva|partita/},
                    {key:"codiceFiscale",label:"Codice Fiscale",indovina:/cod.*fisc/},
                    {key:"indirizzo",label:"Indirizzo (via, o tutto in una riga)",indovina:/indirizzo|sede|^via$/},
                    {key:"civico",label:"N. civico",indovina:/civico|^n\.?\s*$|numero civico/},
                    {key:"cap",label:"CAP",indovina:/^c\.?a\.?p\.?$/},
                    {key:"comune",label:"Comune",indovina:/comune|citt|localit/},
                    {key:"provincia",label:"Provincia",indovina:/^prov|sigla/},
                    {key:"telefono",label:"Telefono",indovina:/telefono|tel\.?$|cellulare/},
                    {key:"email",label:"Email",indovina:/e-?mail/},
                    {key:"referente",label:"Referente",indovina:/referente|contatto/},
                    {key:"note",label:"Note",indovina:/note/},
                  ]}
                  campoObbligatorio="ragioneSociale"
                  avvisoIniziale="Funziona bene se il tuo file ha una riga per ogni cliente, con i dati (ragione sociale, P.IVA, indirizzo...) in colonne. Fogli con celle unite o strutture particolari potrebbero non importarsi correttamente — in quel caso conviene inserirli a mano la prima volta."
                  avvisoFinale="Ogni cliente importato riceve subito il suo numero progressivo (CLI-0XXX), come un cliente creato a mano. Se P.IVA o Codice Fiscale corrispondono a un cliente già esistente, quella riga viene saltata automaticamente — stesso controllo che vale quando crei un cliente a mano."
                  onConferma={async (righeMappate) => {
                    // Riserva subito un blocco di numeri grande quanto il file (anche se
                    // qualche riga verrà poi saltata per duplicato — i numeri riservati e
                    // non usati restano solo un piccolo salto nella sequenza, innocuo).
                    let prossimo = await prossimoBloccoProgressivo(`${effectiveOrgId}_numeroCliente`, righeMappate.length, { orgId: effectiveOrgId }, 99);
                    let creati = 0, saltatiDuplicati = 0;
                    for (const r of righeMappate) {
                      // Stesso controllo anti-duplicati (P.IVA/Codice Fiscale) usato quando
                      // si crea un cliente a mano — un'importazione non deve poterlo aggirare.
                      const pivaClean = r.piva?.trim().replace(/\s/g,"");
                      const cfClean = r.codiceFiscale?.trim().toUpperCase();
                      if (pivaClean) {
                        const snap = await getDocs(query(collection(db,"clienti"), where("organizationId","==",effectiveOrgId), where("piva","==",pivaClean)));
                        if (!snap.empty) { saltatiDuplicati++; continue; }
                      }
                      if (cfClean) {
                        const snap = await getDocs(query(collection(db,"clienti"), where("organizationId","==",effectiveOrgId), where("codiceFiscale","==",cfClean)));
                        if (!snap.empty) { saltatiDuplicati++; continue; }
                      }
                      prossimo++;
                      const numeroCliente = `CLI-${String(prossimo).padStart(4,"0")}`;
                      await addDoc(collection(db,"clienti"), {
                        ragioneSociale:r.ragioneSociale||"", piva:pivaClean||"", codiceFiscale:cfClean||"",
                        ...(()=>{ // indirizzo: colonne separate se ci sono, altrimenti si divide la riga unica
                          const separate = [r.cap, r.comune, r.provincia, r.civico].some(x=>String(x||"").trim());
                          const p = separate ? { via:String(r.indirizzo||"").trim(), civico:String(r.civico||"").trim(), cap:String(r.cap||"").trim(), comune:String(r.comune||"").trim(), provincia:String(r.provincia||"").trim().toUpperCase() } : scomponiIndirizzo(r.indirizzo);
                          return { indirizzoParti:p, indirizzo: componiIndirizzo(p) || String(r.indirizzo||"").trim() };
                        })(),
                        telefono:r.telefono||"", email:r.email||"",
                        referente:r.referente||"", note:r.note||"",
                        numeroCliente, organizationId: effectiveOrgId, createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
                      });
                      creati++;
                    }
                    if (saltatiDuplicati>0) {
                      addToast(`${creati} cliente/i importati ✅ — ${saltatiDuplicati} saltati perché già esistenti (stessa P.IVA o Codice Fiscale)`,"info");
                    } else {
                      addToast(`${creati} cliente/i importati ✅`,"success");
                    }
                  }}
                />
              )}
              <InsightButton onNavigate={onNavigate}/>
              <span style={{fontSize:11.5,fontWeight:500,color:"#5b21b6",background:"#f5f3ff",border:"1px solid #ddd6fe",borderRadius:8,padding:"4px 10px",whiteSpace:"nowrap"}}>
                💜 Clicca su un cliente per aprire la sua scheda completa
              </span>
            </h2>
            <p style={{fontSize:13, color:"#6b7280", marginTop:2}}>{clienti.length} clienti · {commesse.filter(c=>c.clienteId&&!c.isParent).length} commesse collegate</p>
          </div>
        </div>
      </div>

      {/* Filtri */}
      <div style={{background:"#fff", borderBottom:"1px solid #e5e7eb", padding:"10px 24px", display:"flex", gap:10, alignItems:"center", flexShrink:0, flexWrap:"wrap"}}>
        <div style={{position:"relative", flex:1, maxWidth:300}}>
          <Search size={13} style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:"#9ca3af"}}/>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Cerca cliente o referente..."
            style={{width:"100%",paddingLeft:30,paddingRight:10,paddingTop:7,paddingBottom:7,border:"1px solid #e5e7eb",borderRadius:7,fontSize:13,fontFamily:"'Inter',sans-serif",outline:"none",boxSizing:"border-box"}}/>
        </div>
        <select value={filterStato} onChange={e=>setFilterStato(e.target.value)}
          style={{border:"1px solid #e5e7eb",borderRadius:7,padding:"7px 10px",fontSize:13,fontFamily:"'Inter',sans-serif",outline:"none"}}>
          <option value="tutti">Tutti gli stati</option>
          {STATI_CLIENTE.map(s=><option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={()=>setSoloPreferiti(p=>!p)}
          style={{display:"flex", alignItems:"center", gap:5, border:`1px solid ${soloPreferiti?"#f59e0b":"#e5e7eb"}`, borderRadius:7, padding:"6px 12px", background:soloPreferiti?"#fffbeb":"#fff", color:soloPreferiti?"#92400e":"#6b7280", fontSize:13, fontWeight:soloPreferiti?600:400, cursor:"pointer", fontFamily:"'Inter',sans-serif"}}>
          <span style={{color: soloPreferiti?"#f59e0b":"#d1d5db"}}>★</span> Preferiti
        </button>
        <div style={{display:"flex",background:"#f3f4f6",borderRadius:7,border:"1px solid #e5e7eb"}}>
          {[{id:"card",label:"⊞",title:"Vista card"},{id:"lista",label:"☰",title:"Vista elenco"}].map(v=>(
            <button key={v.id} onClick={()=>setVistaClienti(v.id)} title={v.title}
              style={{padding:"5px 10px",border:"none",borderRadius:7,
                background:vistaClienti===v.id?"#7c3aed":"transparent",
                color:vistaClienti===v.id?"#fff":"#6b7280",
                fontSize:15,cursor:"pointer"}}>
              {v.label}
            </button>
          ))}
        </div>
        <span style={{fontSize:12,color:"#9ca3af",marginLeft:"auto"}}>{filtered.length} risultati</span>
      </div>

      {/* Lista */}
      <div style={{flex:1, overflowY:"auto", padding:24}}>
        {filtered.length===0 ? (
          <div style={{textAlign:"center",padding:60,color:"#9ca3af"}}>
            <Building2 size={40} color="#d1d5db" style={{margin:"0 auto 12px",display:"block"}}/>
            <h3 style={{fontSize:16,fontWeight:600,color:"#6b7280",marginBottom:8}}>
              {clienti.length===0 ? "Nessun cliente ancora" : "Nessun risultato"}
            </h3>
            {isAdmin && clienti.length===0 && (
              <button className="btn btn-primary" onClick={()=>setShowModal(true)}>
                <Plus size={14}/> Aggiungi primo cliente
              </button>
            )}
          </div>
        ) : (
          <>{vistaClienti==="lista" && (
            <table style={{borderCollapse:"collapse",width:"auto",minWidth:1350,tableLayout:"fixed",fontSize:12}}>
              <thead style={{position:"sticky",top:0,zIndex:5}}>
                <tr style={{background:"#1e1b4b"}}>
                  <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700,width:80}}>N° Cliente</th>
                  <th style={{padding:"8px 14px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700,width:220}}>Cliente</th>
                  <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700,width:90}}>Stato</th>
                  <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700,width:110}}>Settore</th>
                  <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700,width:110}}>P.IVA</th>
                  <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700,width:110}}>Telefono</th>
                  <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700,width:160}}>Indirizzo</th>
                  <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700,width:180}}>Note</th>
                  {isAdmin && <th style={{padding:"8px 12px",textAlign:"center",color:"#fff",fontSize:10,fontWeight:700,width:50}}></th>}
                  <th style={{padding:"8px 12px",textAlign:"center",color:"#fff",fontSize:10,fontWeight:700,width:80}}>Commesse</th>
                  <th style={{padding:"8px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700,width:160}}>Email</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c,i) => {
                  const nAttive = commesse.filter(p=>p.clienteId===c.id&&!p.isParent&&!p.archived).length;
                  const nTot = commesse.filter(p=>p.clienteId===c.id&&!p.isParent).length;
                  return (
                    <tr key={c.id} onClick={()=>setSelectedCliente(c.id)}
                      style={{background:i%2===0?"#fff":"#fafafa",borderBottom:"1px solid #f3f4f6",cursor:"pointer",transition:"background .1s"}}
                      onMouseOver={e=>e.currentTarget.style.background="#faf5ff"}
                      onMouseOut={e=>e.currentTarget.style.background=i%2===0?"#fff":"#fafafa"}>
                      <td style={{padding:"9px 12px",fontFamily:"monospace",fontWeight:700,color:"#7c3aed",fontSize:11}}>{c.numeroCliente||"—"}</td>
                      <td style={{padding:"9px 14px",fontWeight:700,color:"#111827",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                        <span style={{color:c.preferito?"#f59e0b":"#e5e7eb",marginRight:6,fontSize:12}}>{c.preferito?"★":"☆"}</span>
                        {c.ragioneSociale}
                        {c.referente && <span style={{fontSize:11,color:"#9ca3af",marginLeft:8,fontWeight:400}}>{c.referente}</span>}
                      </td>
                      <td style={{padding:"9px 12px"}}>
                        <span style={{fontSize:11,padding:"2px 8px",borderRadius:12,
                          background:(statoColor[c.stato]||"#9ca3af")+"22",
                          color:statoColor[c.stato]||"#9ca3af",fontWeight:600}}>
                          {c.stato||"—"}
                        </span>
                      </td>
                      <td style={{padding:"9px 12px",color:"#6b7280",fontSize:11}}>{c.settore||"—"}</td>
                      <td style={{padding:"9px 12px",color:"#6b7280",fontSize:11,fontFamily:"monospace"}}>{c.piva||"—"}</td>
                      <td style={{padding:"9px 12px",color:"#6b7280",fontSize:11}}>{c.telefono||"—"}</td>
                      <td style={{padding:"9px 12px",color:"#6b7280",fontSize:11,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={c.indirizzo||""}>{c.indirizzo||"—"}</td>
                      <td style={{padding:"9px 12px",color:"#6b7280",fontSize:11,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontStyle:c.note?"normal":"italic"}} title={c.note||""}>{c.note||"—"}</td>
                      {isAdmin && (
                        <td style={{padding:"9px 12px",textAlign:"center"}}>
                          <button onClick={e=>{e.stopPropagation(); setEditCliente(c); setShowModal(true);}}
                            title="Modifica cliente"
                            style={{background:"none",border:"none",cursor:"pointer",color:"#9ca3af",padding:4,borderRadius:6,display:"inline-flex"}}
                            onMouseOver={e=>{e.currentTarget.style.background="#f3f4f6";e.currentTarget.style.color="#7c3aed";}}
                            onMouseOut={e=>{e.currentTarget.style.background="none";e.currentTarget.style.color="#9ca3af";}}>
                            <Edit2 size={13}/>
                          </button>
                        </td>
                      )}
                      <td style={{padding:"9px 12px",textAlign:"center"}}>
                        <span style={{fontWeight:700,color:"#059669"}}>{nAttive}</span>
                        <span style={{color:"#9ca3af",fontSize:10}}> / {nTot}</span>
                      </td>
                      <td style={{padding:"9px 12px",color:"#6b7280",fontSize:11}}>{c.email||"—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {vistaClienti==="card" && (
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))", gap:14}}>
            {filtered.map(c => {
              const nCommesse = commesse.filter(p=>p.clienteId===c.id&&!p.isParent).length;
              const nAttive = commesse.filter(p=>p.clienteId===c.id&&!p.isParent&&!p.archived).length;
              const valTot = isAdmin ? commesse.filter(p=>p.clienteId===c.id&&!p.isParent).reduce((s,p)=>s+(parseFloat(p.valoreEconomico)||0),0) : 0;
              return (
                <div key={c.id} onClick={()=>setSelectedCliente(c.id)}
                  style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,padding:18,cursor:"pointer",transition:"all 0.15s",borderTop:`3px solid ${statoColor[c.stato]||"#e5e7eb"}`}}
                  onMouseOver={e=>e.currentTarget.style.boxShadow="0 4px 16px rgba(0,0,0,0.08)"}
                  onMouseOut={e=>e.currentTarget.style.boxShadow="none"}>
                  <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:12}}>
                    <div style={{flex:1, minWidth:0}}>
                      <div style={{fontSize:15,fontWeight:700,color:"#111827",marginBottom:2,display:"flex",alignItems:"center",gap:6}}>
                        <span onClick={e=>{e.stopPropagation(); updateDoc(doc(db,"clienti",c.id),{preferito:!c.preferito});}}
                          style={{color: c.preferito?"#f59e0b":"#e5e7eb", fontSize:14, cursor:"pointer", flexShrink:0}}
                          title={c.preferito?"Rimuovi dai preferiti":"Aggiungi ai preferiti"}>
                          {c.preferito ? "★" : "☆"}
                        </span>
                        <span style={{overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{c.ragioneSociale}</span>
                      </div>
                      <div style={{fontSize:12,color:"#6b7280"}}>{c.referente}{c.numeroCliente && <span style={{fontFamily:"monospace",color:"#7c3aed",fontWeight:700,marginLeft:8}}>{c.numeroCliente}</span>}</div>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                      <span style={{fontSize:11,padding:"2px 8px",borderRadius:12,background:(statoColor[c.stato]||"#9ca3af")+"22",color:statoColor[c.stato]||"#9ca3af",fontWeight:600}}>
                        {c.stato}
                      </span>
                      {isAdmin && (
                        <button onClick={e=>{e.stopPropagation(); setEditCliente(c); setShowModal(true);}}
                          title="Modifica cliente"
                          style={{background:"none",border:"none",cursor:"pointer",color:"#d1d5db",padding:2,borderRadius:5,display:"inline-flex"}}
                          onMouseOver={e=>{e.currentTarget.style.color="#7c3aed";}}
                          onMouseOut={e=>{e.currentTarget.style.color="#d1d5db";}}>
                          <Edit2 size={13}/>
                        </button>
                      )}
                    </div>
                  </div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10}}>
                    {c.email && <span style={{fontSize:11,color:"#9ca3af"}}>✉ {c.email}</span>}
                    {c.settore && <span style={{fontSize:11,color:"#9ca3af",marginLeft:8}}>· {c.settore}</span>}
                  </div>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",borderTop:"1px solid #f3f4f6",paddingTop:10}}>
                    <div style={{display:"flex",gap:16}}>
                      <span style={{fontSize:12,color:"#6b7280"}}><strong style={{color:"#111827"}}>{nCommesse}</strong> commesse</span>
                      <span style={{fontSize:12,color:"#059669"}}><strong>{nAttive}</strong> attive</span>
                    </div>
                    {isAdmin && valTot>0 && (
                      <span style={{fontSize:12,fontWeight:700,color:"#059669"}}>
                        € {valTot.toLocaleString("it-IT",{minimumFractionDigits:0})}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          )}</>
        )}
      </div>

      {showModal && (
        <ClienteModal cliente={editCliente} orgId={effectiveOrgId}
          onClose={()=>{ setShowModal(false); setEditCliente(null); }}/>
      )}

    </div>
  );
}
